package com.advaita.cloud.drive.ui.store

import androidx.compose.runtime.derivedStateOf
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateListOf
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.setValue
import com.advaita.cloud.drive.shared.AuthConfigDto
import com.advaita.cloud.drive.shared.CuratedCatalogResponse
import com.advaita.cloud.drive.shared.CuratedCatalogStatus
import com.advaita.cloud.drive.shared.CuratedRunRequest
import com.advaita.cloud.drive.shared.CuratedWorkflowDto
import com.advaita.cloud.drive.shared.CompressResponse
import com.advaita.cloud.drive.shared.ExtractResponse
import com.advaita.cloud.drive.shared.FileDto
import com.advaita.cloud.drive.shared.FileIdentity
import com.advaita.cloud.drive.shared.FileKind
import com.advaita.cloud.drive.shared.MeAdmissionDto
import com.advaita.cloud.drive.shared.PullResponse
import com.advaita.cloud.drive.shared.S3Credential
import com.advaita.cloud.drive.shared.Status
import com.advaita.cloud.drive.shared.UploadSessionStatus
import com.advaita.cloud.drive.shared.UserInfoDto
import com.advaita.cloud.drive.ui.api.CreateTarget
import com.advaita.cloud.drive.ui.api.DeleteResult
import com.advaita.cloud.drive.ui.api.FilesApi
import com.advaita.cloud.drive.ui.api.FolderNotFoundException
import com.advaita.cloud.drive.ui.api.PlainRecord
import com.advaita.cloud.drive.ui.api.RedirectingException
import com.advaita.cloud.drive.ui.api.ResumableUploadStore
import com.advaita.cloud.drive.ui.api.UploadOrchestrator
import com.advaita.cloud.drive.ui.api.UploadSessionLocks
import com.advaita.cloud.drive.ui.api.isDestinationGone
import com.advaita.cloud.drive.ui.api.isRefusal
import com.advaita.cloud.drive.ui.api.userFacingMessage
import com.advaita.cloud.drive.ui.components.FormField
import com.advaita.cloud.drive.ui.components.FormFieldOption
import com.advaita.cloud.drive.ui.components.FormFieldType
import com.advaita.cloud.drive.ui.components.ModalRequest
import com.advaita.cloud.drive.ui.components.ToastItem
import com.advaita.cloud.drive.ui.components.ShareCap
import com.advaita.cloud.drive.ui.components.ToastLevel
import com.advaita.cloud.drive.ui.components.admissionPollWanted
import com.advaita.cloud.drive.ui.components.afterAdmissionPoll
import com.advaita.cloud.drive.ui.components.afterRefusalPoll
import com.advaita.cloud.drive.ui.components.refusalPollWanted
import com.advaita.cloud.drive.ui.components.capForMe
import com.advaita.cloud.drive.ui.components.shareControlShown
import com.advaita.cloud.drive.ui.components.shareUiShown
import kotlinx.browser.document
import kotlinx.browser.localStorage
import kotlinx.browser.window
import kotlinx.coroutines.CancellationException
import kotlinx.coroutines.CompletableDeferred
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.Job
import kotlinx.coroutines.MainScope
import kotlinx.coroutines.async
import kotlinx.coroutines.awaitAll
import kotlinx.coroutines.coroutineScope
import kotlinx.coroutines.delay
import kotlinx.coroutines.launch
import kotlinx.coroutines.sync.Semaphore
import kotlinx.coroutines.sync.withPermit
import kotlin.js.Date
import org.w3c.files.File

/** How long a confirmation/progress toast stays up. */
const val DEFAULT_TOAST_MS = 4_000L

/** How long an ERROR toast stays up — long enough to read a bucket + object path. */
const val ERROR_TOAST_MS = 12_000L

/** Cap on simultaneously-rendered toasts, so a per-item bulk failure can't bury the page. */
const val MAX_VISIBLE_TOASTS = 4

/** Percent-encode one URL path segment (JS encodeURIComponent) — mirror of the
 *  decode in Main.kt::pathFromLocation so folder paths round-trip.
 *
 *  Grab the GLOBAL `encodeURIComponent` function reference and invoke it with the
 *  argument, rather than inlining `encodeURIComponent(value)` as JS source: the
 *  inline form relies on the compiler emitting a local literally named `value`,
 *  which the production webpack/terser build can minify-rename → `ReferenceError`
 *  at runtime (silently breaking every folder navigation). A global identifier is
 *  never renamed. (PR #41 review.) */
private fun encodeUriComponent(value: String): String =
    js("encodeURIComponent").unsafeCast<(String) -> String>().invoke(value)

/** ADV-1330 F3, CR-6.3: the lists the workspace can show. */
enum class DriveView { MY_DRIVE, SHARED, SHARED_BY_ME }

/** ADV-1330 F3: the URL query that opens Shared with me. */
const val SHARED_VIEW_QUERY = "?view=shared"

/** CR-6.3 (D10): the URL query that opens Shared by me. */
const val SHARED_BY_ME_VIEW_QUERY = "?view=shared-by-me"

/**
 * True when a URL's query ([search], as `location.search`) asks for Shared with me. Parsed with
 * `URLSearchParams`, so `?view=shared&x=1` and `?x=1&view=shared` both count.
 */
fun isSharedViewUrl(search: String): Boolean {
    // Global references only, never an inlined local name (see encodeUriComponent): the
    // production minifier may rename a local, and a global is never renamed.
    val params: dynamic = jsGlobal.Reflect.construct(jsGlobal.URLSearchParams, arrayOf(search))
    return params.get(VIEW_PARAM) == SHARED_VIEW_VALUE
}

/** True when a URL's query asks for Shared by me (D10). */
fun isSharedByMeUrl(search: String): Boolean {
    val params: dynamic = jsGlobal.Reflect.construct(jsGlobal.URLSearchParams, arrayOf(search))
    return params.get(VIEW_PARAM) == SHARED_BY_ME_VIEW_VALUE
}

/** CR-6.5: true when a URL's query asks for the My access dialog (`?dialog=my-access`). */
fun isMyAccessUrl(search: String): Boolean = MY_ACCESS_QUERY.containsMatchIn(search)

private val MY_ACCESS_QUERY = Regex("(^|[?&])dialog=my-access(&|$)")
private val SHARED_BY_ME_VIEW_VALUE: String = "shared-by-me"

private val jsGlobal: dynamic = js("globalThis")

/**
 * The view a URL lands on (on load, and on Back or Forward). Shared with me only while the share
 * UI shows (the frontend build ruling, item 1): with sharing off, a `?view=shared` link or a Back
 * to one lands on My Drive, so no `/api/shared` read and no "not on yet" sentence.
 */
fun viewForUrl(search: String, shareUiVisible: Boolean): DriveView = when {
    !shareUiVisible -> DriveView.MY_DRIVE
    isSharedByMeUrl(search) -> DriveView.SHARED_BY_ME
    isSharedViewUrl(search) -> DriveView.SHARED
    else -> DriveView.MY_DRIVE
}

private val VIEW_PARAM: String = "view"
private val SHARED_VIEW_VALUE: String = "shared"

/**
 * ADV-1326c (RULING 2026-09-28 (1)): how long the web waits after `/api/me` before its one ask
 * of `/api/me/admission`. The relay's report is one vault call, so 2 s covers it in normal use.
 */
internal const val ADMISSION_POLL_DELAY_MS = 2_000L

/** ADV-1463: the state of one item in a bulk delete. */
enum class BulkItemState { WAITING, DELETING, DONE, FAILED }

/** ADV-1463: one row in the [BulkDeleteState] pop-up. */
data class BulkDeleteItem(val id: String, val name: String, val state: BulkItemState = BulkItemState.WAITING, val reason: String? = null)

/** ADV-1463: what the bulk-delete pop-up shows. */
data class BulkDeleteState(val items: List<BulkDeleteItem>) {
    val total: Int get() = items.size
    val finished: Int get() = items.count { it.state == BulkItemState.DONE || it.state == BulkItemState.FAILED }
    val failed: Int get() = items.count { it.state == BulkItemState.FAILED }
    val over: Boolean get() = finished == total
}

/** ADV-1463: the deletes that one bulk delete sends at once. */
internal const val BULK_DELETE_CONCURRENCY = 4

/** ADV-1463: how long the pop-up stays after a bulk delete with no failure. */
internal const val BULK_DELETE_CLOSE_MS = 3_000L

/**
 * [sharedWithMe] is Shared with me's own store. This store reads its open folder to know where a
 * create goes ([createTarget]), and reloads it after one (ADV-1330, the shared-folder create slice).
 */
class FileBrowserStore(val sharedWithMe: SharedWithMeStore) {

    init {
        // ADV-1330: Shared with me's background re-reads stop once the person leaves it.
        sharedWithMe.shown = { view == DriveView.SHARED }
    }

    var files: List<FileDto> by mutableStateOf(emptyList())
        private set

    var currentPath: String by mutableStateOf("/")
        private set

    // Starts true: the first composition happens BEFORE the mount effect runs the
    // initial refresh(), so a `false` default would flash the "This folder is empty"
    // state for one frame on page load/refresh. We are loading until the first fetch resolves.
    var isLoading: Boolean by mutableStateOf(true)
        private set

    /**
     * True once a 401 has sent us to /auth/login. ADV-1155.
     *
     * The startup chain aborts on the first 401 now (the loaders rethrow
     * [com.advaita.cloud.drive.ui.api.RedirectingException]), which means `refresh()` — the only
     * thing that ever sets [isLoading] back to false — does not run. [isLoading] starts true, so
     * nothing else would change Compose state and nothing would recompose: the UI sat on
     * "Loading…" forever, with no sign-in button, whenever the navigation failed to commit. And
     * it can fail to commit — `installBeforeUnloadWarning` raises the native "Leave site?"
     * prompt during an upload and the user may click "Stay".
     *
     * Setting this is what triggers the recomposition, so [com.advaita.cloud.drive.ui.App] can
     * offer a way out instead of a dead spinner. Deliberately NOT reusing [error]: this is not
     * a failure, it is a navigation in progress.
     */
    var signingIn: Boolean by mutableStateOf(false)
        private set

    /** Called from the composition boundary when a 401 aborted the startup chain. ADV-1155. */
    fun signInStarted() {
        signingIn = true
        isLoading = false
    }

    /** True while a POST /api/pull is in flight — the server vets each URL + creates a row
     *  per URL, so an 11-URL submit takes a few seconds. Drives the header button's
     *  spinner/disabled state so the click has immediate feedback. */
    var pullSubmitting: Boolean by mutableStateOf(false)
        private set

    /**
     * Ids of the rows the user has ticked in the file table (Google-Drive-style bulk
     * selection). A plain immutable Set held in a mutableStateOf — reassignment is the
     * recomposition signal, so never mutate in place. Cleared on folder navigation and
     * after a compress is enqueued; pruned on every re-list so a row that disappeared
     * (deleted elsewhere, or by us) can't stay selected and 404 a later action.
     */
    var selectedIds: Set<String> by mutableStateOf(emptySet())
        private set

    /** True while a POST /api/archive/compress is in flight — drives the action bar's
     *  disabled state so the click has immediate feedback (mirrors [pullSubmitting]). */
    var compressSubmitting: Boolean by mutableStateOf(false)
        private set

    /** File ids with a POST /api/archive/extract in flight — per-row analogue of
     *  [compressSubmitting] (a whole-selection submit has one flag; an extract is
     *  fired per row, so it needs one flag per id). Drives that row's 📂 disabled
     *  state so a double-click can't queue two extracts of the same archive. */
    var extractingIds: Set<String> by mutableStateOf(emptySet())
        private set

    /** True while a bulk selection-bar action (delete, download) is running. Disables
     *  every bar button for the duration, so a batch cannot be started twice or
     *  interleaved with another batch over a selection it is already consuming. */
    var bulkBusy: Boolean by mutableStateOf(false)
        private set

    /** ADV-1465: the last row a click without Shift ticked or unticked. Null after a clear or a sort change. */
    private var selectionAnchor: String? = null

    fun toggleSelected(id: String) {
        selectedIds = if (id in selectedIds) selectedIds - id else selectedIds + id
        selectionAnchor = id
    }

    /**
     * ADV-1465: a checkbox click. With [shift] and an anchor in [order] (the rows as the table
     * shows them), the rows from the anchor to [id] are ticked. Else the click toggles [id] and
     * [id] becomes the anchor.
     */
    fun clickSelect(id: String, shift: Boolean, order: List<String>) {
        val anchor = selectionAnchor
        if (shift && anchor != null && anchor in order) {
            selectedIds = rangeSelect(selectedIds, anchor, id, order)
        } else {
            toggleSelected(id)
        }
    }

    /** Header checkbox: select every row currently listed, or clear them if all are
     *  already selected. "Visible" means loaded in this folder — there is no paging. */
    fun toggleSelectAllVisible() {
        val visible = files.map { it.id }.toSet()
        if (visible.isEmpty()) return
        if (selectedIds.containsAll(visible)) {
            selectedIds = selectedIds - visible
            // ADV-1465 review: a header clear also clears the Shift+click anchor.
            selectionAnchor = null
        } else {
            selectedIds = selectedIds + visible
        }
    }

    fun clearSelection() {
        selectionAnchor = null
        if (selectedIds.isNotEmpty()) selectedIds = emptySet()
    }

    // ---- Tim (2026-09-28): the selection bar in My Drive and in a shared folder ---------------

    /** The rows of the list on screen: My Drive's folder, or the open shared folder. */
    val selectionRows: List<FileDto> get() = if (view == DriveView.SHARED) sharedWithMe.items else files

    /** The ticked ids of the list on screen. Shared with me keeps its own ([SharedWithMeStore.selectedIds]). */
    val selectionIds: Set<String> get() = if (view == DriveView.SHARED) sharedWithMe.selectedIds else selectedIds

    /** Clears the selection of the list on screen (the bar's ✕). */
    fun clearViewSelection() {
        if (view == DriveView.SHARED) sharedWithMe.clearSelection() else clearSelection()
    }

    /** Clears the selection of the list a bulk action ran in. */
    private fun clearSelectionOf(target: CreateTarget) {
        if (target is CreateTarget.SharedFolder) sharedWithMe.clearSelection() else clearSelection()
    }

    /**
     * True when Shared with me shows the selection column: on the roots list and inside a
     * folder, the same as My Drive (Tim, 2026-09-28), and, as in My Drive, only with the archive
     * feature on. The bar then offers what the backend accepts there ([selectionBarActions]).
     */
    fun sharedSelectionShown(archiveEnabled: Boolean): Boolean = archiveEnabled && view == DriveView.SHARED

    /**
     * The selection bar's actions for the list on screen: My Drive's ([selectionActions] with no
     * grants), a shared folder's (its root's grants), or the roots list's ([rootsSelectionActions],
     * each picked root's own grants).
     */
    val selectionBarActions: SelectionActions
        get() = when {
            view != DriveView.SHARED -> selectionActions(selectionRows, selectionIds, null)
            sharedWithMe.trail.isEmpty() ->
                rootsSelectionActions(selectionRows, selectionIds, capsOf = { sharedWithMe.rowCapabilities(it) })
            else -> selectionActions(selectionRows, selectionIds, sharedWithMe.currentCapabilities)
        }

    /** Drop ids that are no longer in the listing (deleted, or we navigated). */
    private fun pruneSelection() {
        if (selectedIds.isEmpty()) return
        val visible = files.map { it.id }.toSet()
        val kept = selectedIds.intersect(visible)
        if (kept.size != selectedIds.size) selectedIds = kept
    }

    /**
     * True while the workflow list is being fetched, so "Run workflow" can show it is
     * working. `wispResponseTimeout` is 10s, so an unreachable registry previously meant
     * ten seconds of a UI that looked idle — the click appeared to do nothing at all, and
     * the scientist's only reasonable conclusion was that the button was broken.
     */
    var opcCatalogLoading: Boolean by mutableStateOf(false)
        private set

    var error: String? by mutableStateOf(null)
        private set

    var notFound: Boolean by mutableStateOf(false)
        private set

    var currentUser: UserInfoDto? by mutableStateOf(null)
        private set

    /**
     * The cap that the session puts on every Share control (S8 rows 1, 5 and 9), or null. Until
     * `/api/me` answers, sharing counts as off, so no Share control is live before the gate is known.
     */
    val sessionShareCap: ShareCap? get() = capForMe(currentUser)

    /**
     * ADV-1330 (the frontend build ruling, item 1): the Shared with me tab shows only once
     * `/api/me` says `sharingEnabled`. Before it answers, or if it fails, the tab does not show
     * at all, and never with the "not on yet" sentence. Since D7 this controls the tab only. The
     * Share control reads [shareControlVisible].
     */
    val shareUiVisible: Boolean get() = shareUiShown(currentUser)

    /**
     * ADV-1330 D7 Q1: the Share control shows only when [shareUiVisible] holds and the token
     * carried an `org_shareable` claim ([shareControlShown]). The Shared with me tab keeps
     * [shareUiVisible].
     */
    val shareControlVisible: Boolean get() = shareControlShown(currentUser)

    var authConfig: AuthConfigDto? by mutableStateOf(null)
        private set

    /**
     * Durable, per-user resume store. Non-null only after a successful resume-key
     * fetch on the S3 backend (see [loadAuthConfig]); stays null (resume disabled)
     * on the local-fs backend or if the key endpoint 503s. Every resume path
     * null-guards on it, and it is passed as the orchestrator's `store` arg so
     * fresh uploads persist resumable sessions.
     */
    var resumableStore: ResumableUploadStore? = null
        private set

    /** Server-validated resumable sessions for the current user (drives the Resume panel). */
    val resumableCandidates = mutableStateListOf<PlainRecord>()

    /** Set when a Resume file-pick doesn't match the recorded identity; surfaced in the panel. */
    var resumeError: String? by mutableStateOf(null)
        private set

    /** Aggregate counters for the "Uploading N of M" header line. */
    var inFlightUploads: Int by mutableStateOf(0)
        private set
    var completedUploads: Int by mutableStateOf(0)
        private set

    /** Set when uploadAll detects a folder upload (items carry relativePath). The
     *  UI shows "Uploading folder \"$name\"" instead of the generic header. */
    var currentUploadFolderName: String? by mutableStateOf(null)
        private set

    /**
     * Per-file progress for every in-flight upload. UploadOrchestrator writes
     * one entry per file via [putUploadProgress] keyed by a stable id assigned
     * at the start of that file's run, and [removeUploadProgress] clears it
     * when the file finishes (success or failure). The UI iterates these to
     * render one progress line per concurrent upload.
     */
    data class FileUploadProgress(
        val id: Long,
        val name: String,
        val bytesDone: Long,
        val totalBytes: Long,
        val partsDone: Int?,
        val partsInFlight: Int?,
        val partsTotal: Int?,
        val bytesPerSecond: Double?,
        val etaSeconds: Long?,
        /** True during the multipart warmup window (first 60s, no parts confirmed
         *  yet). UI suppresses real progress and shows "initializing…" while true. */
        val inWarmup: Boolean = false,
    )

    /** ADV-1463: the bulk-delete pop-up, or null when it is closed. */
    var bulkDelete: BulkDeleteState? by mutableStateOf(null)
        private set

    fun closeBulkDelete() { bulkDelete = null }

    val activeUploads = mutableStateListOf<FileUploadProgress>()
    private var nextUploadId: Long = 0L

    fun newUploadProgressId(): Long = ++nextUploadId

    fun putUploadProgress(p: FileUploadProgress) {
        val idx = activeUploads.indexOfFirst { it.id == p.id }
        if (idx >= 0) activeUploads[idx] = p else activeUploads.add(p)
    }

    fun removeUploadProgress(id: Long) {
        activeUploads.removeAll { it.id == id }
    }

    /** The zero/initial progress row shown before the first orchestrator emit. */
    private fun initialProgressRow(id: Long, name: String, totalBytes: Long): FileUploadProgress =
        FileUploadProgress(
            id = id, name = name,
            bytesDone = 0L, totalBytes = totalBytes,
            partsDone = null, partsInFlight = null, partsTotal = null,
            bytesPerSecond = null, etaSeconds = null,
        )

    /** Map an orchestrator [UploadOrchestrator.Progress] emit to a progress row. */
    private fun progressRow(id: Long, name: String, p: UploadOrchestrator.Progress): FileUploadProgress =
        FileUploadProgress(
            id = id, name = name,
            bytesDone = p.bytesDone, totalBytes = p.bytesTotal,
            partsDone = p.partsDone,
            partsInFlight = p.partsInFlight,
            partsTotal = p.partsTotal,
            bytesPerSecond = p.bytesPerSecond,
            etaSeconds = p.etaSeconds,
            inWarmup = p.inWarmup,
        )

    var activeModal: ModalRequest? by mutableStateOf(null)
        private set

    val toasts = mutableStateListOf<ToastItem>()
    private var nextToastId: Long = 0L

    // --- ADV-1215 UI: client-side column sort for the folder view + search-result highlight. ---

    var sortKey: TableSortKey by mutableStateOf(TableSortKey.NAME)
        private set

    var sortAscending: Boolean by mutableStateOf(true)
        private set

    private val sortedFilesState = derivedStateOf { sortedForTable(files, sortKey, sortAscending) }

    /** Rows in table order. The server returns name order; any other order is the table's. */
    val sortedFiles: List<FileDto> get() = sortedFilesState.value

    /** Header click: a new key starts at its natural direction; the active key flips. */
    fun onSortHeaderClick(key: TableSortKey) {
        // ADV-1465: a re-sort changes the rows' screen order, so a Shift+click anchor from
        // before it no longer names a meaningful range in either list.
        selectionAnchor = null
        sharedWithMe.resetSelectionAnchor()
        if (key == sortKey) sortAscending = !sortAscending
        else { sortKey = key; sortAscending = defaultAscending(key) }
    }

    /** Row to scroll-to + flash after a search navigation. Consumed once by FileTable. */
    var pendingHighlightId: String? by mutableStateOf(null)
        private set

    fun highlightConsumed() { pendingHighlightId = null }

    /**
     * ADV-1330 (Tim, 2026-09-28): a search result in a shared folder. It opens the result's
     * folder inside the shared trail ([SharedWithMeStore.revealPath]) and flashes [fileId] (null
     * for a folder result), as [revealFile] does in My Drive.
     */
    fun revealShared(folderPath: String, fileId: String?): Job {
        pendingHighlightId = fileId
        return MainScope().launch {
            when (sharedWithMe.revealPath(folderPath)) {
                RevealResult.OPENED -> Unit
                // A move during the walk wins, and flashes nothing.
                RevealResult.INTERRUPTED -> pendingHighlightId = null
                RevealResult.GONE -> {
                    pendingHighlightId = null
                    toast(SEARCH_RESULT_GONE, ToastLevel.INFO)
                }
            }
        }
    }

    /**
     * A search result click: a shared search opens it inside the shared trail
     * ([revealShared]), and a My Drive search reveals it in My Drive ([revealFile]). Returns the
     * shared walk's job, or null.
     */
    fun pickSearchResult(search: SearchStore, picked: FileDto): Job? {
        val target = search.targetFolderOf(picked)
        val highlight = if (picked.kind == FileKind.FOLDER) null else picked.id
        if (search.sharedFolder != null) return revealShared(target, highlight)
        revealFile(target, highlight)
        return null
    }

    /**
     * Search-result click: land in [folderPath] and flash [fileId] (null for a
     * folder result — just navigate). navigateTo no-ops when already there; the
     * highlight effect in FileTable still fires off the state change.
     */
    fun revealFile(folderPath: String, fileId: String?) {
        pendingHighlightId = fileId
        navigateTo(folderPath)
    }

    suspend fun refresh() {
        console.log("[drive] refresh() called for path=$currentPath; activeUploads=${activeUploads.size}")
        isLoading = true
        error = null
        notFound = false
        try {
            files = FilesApi.listFiles(currentPath)
            pruneSelection()
            schedulePollIfProcessing()
        } catch (e: FolderNotFoundException) {
            files = emptyList()
            notFound = true
        } catch (e: RedirectingException) {
            throw e   // control flow, not an error: we are navigating to /auth/login. ADV-1155.
        } catch (e: Throwable) {
            error = "Couldn't load this folder. ${e.userFacingMessage()}"
            console.error("FileBrowserStore.refresh error:", e)
        } finally {
            isLoading = false
        }
    }

    /** In-flight poll for pipeline post-processing, so async outputs (e.g. FastQC
     *  reports) appear and PROCESSING badges clear without a manual refresh. */
    private var pollJob: Job? = null
    private val pollIntervalMs = 3000L

    /**
     * If any visible item is still PENDING/PROCESSING, quietly re-list after a
     * short delay; that re-list re-invokes this, so it loops until everything is
     * terminal (READY/FAILED) — then no poll is scheduled and it stops on its own.
     * Cancels any prior poll and bails if the user navigated away.
     */
    private fun schedulePollIfProcessing() {
        pollJob?.cancel()
        // Gated on the flag itself, not just presence of archiveProgress: if archive is
        // switched OFF after a job was enqueued (rollback scenario), the poller no-ops
        // on that PENDING/RUNNING job forever, so an ungated clause here would re-list
        // /api/files every pollIntervalMs indefinitely for a row that will never resolve.
        val archiveEnabled = authConfig?.archiveEnabled == true
        val processing = files.count {
            it.status == Status.PROCESSING || it.status == Status.PENDING ||
                it.pullProgress?.status == "PENDING" || it.pullProgress?.status == "RUNNING" ||
                (archiveEnabled && (it.archiveProgress?.status == "PENDING" || it.archiveProgress?.status == "RUNNING")) ||
                it.deleteProgress?.status == "PENDING" || it.deleteProgress?.status == "RUNNING"
        }
        if (processing == 0) return
        console.log("[drive] $processing item(s) still processing — re-checking in ${pollIntervalMs}ms")
        val pathAtSchedule = currentPath
        pollJob = MainScope().launch {
            delay(pollIntervalMs)
            if (currentPath != pathAtSchedule) return@launch
            quietRefresh()
        }
    }

    /**
     * Re-fetch the file list without flipping `isLoading` (so the table doesn't
     * flicker between "Loading…" and the rows). Called after each individual
     * upload finishes so newly committed files (and the destination folder
     * materialized by ensureFolderChain) appear incrementally.
     */
    private suspend fun quietRefresh() {
        try {
            files = FilesApi.listFiles(currentPath)
            pruneSelection()
            schedulePollIfProcessing()
        } catch (e: Throwable) {
            console.error("[drive] quietRefresh error:", e)
        }
    }

    /** [fetch] is a test seam with the production default. */
    internal suspend fun loadCurrentUser(fetch: suspend () -> UserInfoDto = { FilesApi.fetchMe() }) {
        try {
            currentUser = fetch()
        } catch (e: RedirectingException) {
            throw e   // control flow, not an error: we are navigating to /auth/login. ADV-1155.
        } catch (e: Throwable) {
            console.error("FileBrowserStore.loadCurrentUser error:", e)
        }
    }

    /**
     * ADV-1326c (RULING 2026-09-28 (1)): when `/api/me` answered `admission: null`, wait
     * [ADMISSION_POLL_DELAY_MS] and ask `/api/me/admission` once. A hold in the answer
     * replaces [currentUser], so the App switches to the Pending or no-access page. A null
     * answer or a failure changes nothing, so the page stays usable (the P1.7 outage posture).
     * It asks once and never again. Example: Acme turns auto_admission off, and Sam signs in. His
     * first page shows the file browser, and 2 s later it shows the Pending page.
     *
     * [fetch] is a test seam with the production default. A [RedirectingException] passes
     * through, as in [loadCurrentUser].
     */
    internal suspend fun pollAdmissionOnce(
        fetch: suspend () -> MeAdmissionDto = admissionFetch,
    ) {
        if (!admissionPollWanted(currentUser)) return
        delay(ADMISSION_POLL_DELAY_MS)
        val polled = try {
            fetch().admission
        } catch (e: RedirectingException) {
            throw e   // control flow, not an error: we are navigating to /auth/login. ADV-1155.
        } catch (e: Throwable) {
            console.error("FileBrowserStore.pollAdmissionOnce error:", e)
            return
        }
        afterAdmissionPoll(currentUser, polled)?.let { currentUser = it }
    }

    /**
     * ADV-1326c (fresh-eyes L5): true once a re-poll after a refusal has run on this page load.
     * A re-poll that finds a hold moves the page, and one that finds none, or fails, says the
     * next refusal will find the same. So later refusals just toast. Example: guest Lena drops
     * 50 files at her root, and the web polls one time, not 50. A reload starts a new store.
     */
    private var refusalPollSpent = false

    /** ADV-1326c: how both polls ask `/api/me/admission`. A test seam with the production default. */
    internal var admissionFetch: suspend () -> MeAdmissionDto = { FilesApi.fetchMeAdmission() }

    /**
     * ADV-1326c (ruling 6): after a creation refused with [refusal], poll `/api/me/admission`
     * once, with no delay, when [refusalPollWanted] says so. True when the answer was a hold and
     * the page moved to the hold page: the caller then shows no toast. False in every other case,
     * a null answer and a failure included, and the caller shows its toast as before. One page
     * load polls at most once ([refusalPollSpent]), so a refusal never starts a loop.
     */
    internal suspend fun repollAfterRefusal(
        refusal: Throwable,
        fetch: suspend () -> MeAdmissionDto = admissionFetch,
    ): Boolean {
        if (refusalPollSpent || !refusalPollWanted(currentUser, refusal)) return false
        // fresh-eyes L5: one re-poll that finds no hold is enough for this page load.
        refusalPollSpent = true
        val polled = try {
            fetch().admission
        } catch (e: RedirectingException) {
            throw e   // control flow, not an error: we are navigating to /auth/login. ADV-1155.
        } catch (e: Throwable) {
            console.error("FileBrowserStore.repollAfterRefusal error:", e)
            return false
        }
        val next = afterRefusalPoll(currentUser, polled) ?: return false
        currentUser = next
        return true
    }

    suspend fun loadAuthConfig() {
        try {
            authConfig = FilesApi.fetchAuthConfig()
        } catch (e: RedirectingException) {
            throw e   // control flow, not an error: we are navigating to /auth/login. ADV-1155.
        } catch (e: Throwable) {
            console.error("FileBrowserStore.loadAuthConfig error:", e)
        }
        // Resume is only meaningful on the S3/presigned backend, and only once we
        // know who the user is (the store is scoped + encrypted per org+user).
        // App.kt loads currentUser before authConfig, so both are available here.
        // Guarding on the key fetch means a 503 disables resume with no records
        // written and no store opened.
        val user = currentUser
        if (authConfig?.storageBackend == "s3" && user != null) {
            runCatching {
                val key = FilesApi.getResumeKey().keyBase64
                ResumableUploadStore().also { it.init(key, user.orgId, user.username) }
            }.onSuccess { resumableStore = it; scanResumable() }
             .onFailure { console.log("[drive] resume disabled: ${it.message}") }
        }
    }

    /**
     * Scan the durable store for the current user's sessions and keep only those the
     * SERVER still considers resumable (INITIATED with parts remaining). Everything
     * else is deleted from the store. The server is authoritative — a session the
     * store believes is pending but that completed/aborted/expired server-side is
     * pruned here so a stale row can never be offered for resume.
     *
     * The server can't tell "interrupted" from "uploading right now in another
     * tab" (both are INITIATED with parts remaining), so records whose session
     * Web Lock is held are skipped: they're live somewhere. A queued waiter on
     * each skipped lock re-runs this scan the moment its holder releases —
     * normal finish prunes the record, a crash/close surfaces the Resume panel
     * here without a reload.
     */
    fun scanResumable(): Job = MainScope().launch {
        val store = resumableStore ?: return@launch
        store.pruneForeign()
        val live = UploadSessionLocks.heldSessions()
        var skippedLive = 0
        val alive = mutableListOf<PlainRecord>()
        for (rec in store.listMine()) {
            if (rec.sessionId in live) {
                skippedLive++
                UploadSessionLocks.onReleased(rec.sessionId) { scanResumable() }
                continue
            }
            // Delete ONLY on a definitive terminal server response. A thrown error
            // (transient 5xx / offline during reopen) must KEEP the record so a
            // still-valid resumable session isn't permanently dropped; it just
            // won't be offered this round.
            val status = runCatching { FilesApi.getUploadStatus(rec.sessionId) }.getOrNull()
            when {
                status == null -> {}  // transient/unreachable — keep, don't offer this round
                status.status == UploadSessionStatus.INITIATED && status.partsRemaining.isNotEmpty() ->
                    alive.add(rec)
                else -> store.delete(rec.sessionId)  // COMPLETED/ABORTED/no-parts — definitively done
            }
        }
        resumableCandidates.clear()
        resumableCandidates.addAll(alive)
        console.log("[drive] resume-scan: offered=${alive.size} liveElsewhere=$skippedLive")
    }

    /** Abort the server session (best-effort) and forget the local record. */
    fun discardResumable(rec: PlainRecord) = MainScope().launch {
        runCatching { FilesApi.abortUpload(rec.sessionId) }
        resumableStore?.delete(rec.sessionId)
        resumableCandidates.remove(rec)
        // Clear any stale mismatch message so it doesn't linger after the last
        // candidate is discarded.
        resumeError = null
    }

    /**
     * Resume an interrupted upload after the user re-selects the file. The picked
     * file must match the recorded identity (name+size+lastModified); otherwise we
     * surface [resumeError] and bail rather than uploading the wrong bytes into an
     * existing session. Progress rows reuse the exact same mapping as fresh uploads.
     */
    fun resumeUpload(rec: PlainRecord, file: File) = MainScope().launch {
        val store = resumableStore ?: return@launch
        resumeError = null
        val picked = FileIdentity(
            file.name,
            file.size.toString().toLongOrNull() ?: 0L,
            (file.asDynamic().lastModified as Double).toLong(),
        )
        if (!rec.identity.matches(picked)) {
            resumeError = "That's not the same file — pick ${rec.identity.name}"
            return@launch
        }
        // Drop the candidate NOW, as the resume starts — not when it finishes.
        // The session is live again from this point (the orchestrator holds its
        // Web Lock), so it must stop offering itself as "interrupted" while the
        // resumed upload runs. On failure we re-scan below to re-offer it.
        resumableCandidates.remove(rec)
        val uploadId = newUploadProgressId()
        // Show a row immediately (before the first progress emit) so the strip
        // reflects the in-flight resume; size comes from the recorded identity.
        putUploadProgress(initialProgressRow(uploadId, rec.identity.name, rec.identity.size))
        val orchestrator = UploadOrchestrator(store = store)
        try {
            finishResume(rec) {
                orchestrator.resume(file, rec) { p ->
                    putUploadProgress(progressRow(uploadId, rec.identity.name, p))
                }
            }
        } finally {
            removeUploadProgress(uploadId)
        }
    }

    /**
     * Runs one resume ([resume] is the orchestrator's, and the test seam) and settles it.
     * ADV-1330 (Question 4 (a), fresh-eyes L-5): the session already names its destination. A
     * shared one reopens that shared folder and waits for the row, as a fresh upload does, and
     * an older record (no folder id) lists My Drive. A failure goes through [createFailed], so a
     * shared refusal reads the grants again, and [rescan] re-offers a session that is still
     * resumable (the orchestrator released its lock).
     */
    internal suspend fun finishResume(
        rec: PlainRecord,
        rescan: () -> Unit = { scanResumable() },
        resume: suspend () -> FileDto,
    ) {
        val destination = rec.destination
        try {
            val row = resume()
            reloadAfterCreate(destination)
            awaitNewRows(destination, listOf(uploadedRowListed(row, rec.relativePath)))
        } catch (t: Throwable) {
            console.error("[drive] resume failed: ${rec.identity.name}", t)
            createFailed(destination, t, "Upload failed for ${rec.identity.name}.")
            rescan()
        }
    }

    fun navigateTo(path: String) {
        val leavingShared = view != DriveView.MY_DRIVE
        if (path == currentPath && !leavingShared) return
        clearSelection()
        view = DriveView.MY_DRIVE
        currentPath = path
        pushUrl(path)
        MainScope().launch { refresh() }
    }

    /**
     * Set the path WITHOUT pushing a new history entry. Used by the popstate
     * listener so a browser back/forward doesn't generate a new entry.
     */
    fun setPathFromUrl(path: String) {
        val leavingShared = view != DriveView.MY_DRIVE
        if (path == currentPath && !leavingShared) return
        clearSelection()
        view = DriveView.MY_DRIVE
        currentPath = path
        MainScope().launch { refresh() }
    }

    /**
     * ADV-1330 F3: which list the workspace shows. [DriveView.SHARED] is Shared with me, which
     * [SharedWithMeStore] owns. My Drive keeps [currentPath], so the way back lands where the
     * user was.
     */
    var view: DriveView by mutableStateOf(DriveView.MY_DRIVE)
        private set

    /**
     * ADV-1330 (the shared-folder create slice, scope 1): where New folder, Upload files, Upload
     * folder, Pull from URL and Run workflow go, read from the view. My Drive gives its
     * [currentPath]. Shared with me gives its open folder, and null at its top level, where there
     * is nothing open to add to, and while the view shows no folder (loading, an error, a cap). Example: Lena opens Run 42/raw from Shared with me, and her
     * upload goes into raw by id.
     */
    val createTarget: CreateTarget?
        get() = when (view) {
            DriveView.MY_DRIVE -> CreateTarget.MyDrive(currentPath)
            // Only while the open folder is what the view shows: not while a folder loads (the
            // toolbar would target the parent), and not over an error or a cap (fresh-eyes M-1).
            DriveView.SHARED -> sharedWithMe.takeIf { !it.loading && it.error == null && it.listCap == null }
                ?.currentFolder?.let { CreateTarget.SharedFolder(it.id, it.displayName) }
            // CR-6.3: Shared by me is a flat list of grants. Nothing is created there.
            DriveView.SHARED_BY_ME -> null
        }

    /**
     * The empty state of Shared with me: the roots list's own line, else My Drive's folder line
     * where the person may add files (Upload works there), else a plain "This folder is empty."
     */
    val sharedEmptyText: String
        get() = when {
            sharedWithMe.trail.isEmpty() -> "Nothing is shared with you yet."
            createActions.uploadFiles.enabled -> EMPTY_FOLDER_TEXT
            else -> "This folder is empty."
        }

    /** ADV-1330: the toolbar's create buttons for [createTarget] ([createActions]). */
    val createActions: CreateActions
        get() = createActions(
            createTarget,
            sharedWithMe.currentCapabilities,
            sharedWithMe.grantsStatus,
            rootName = sharedWithMe.trail.firstOrNull()?.displayName,
        )

    /**
     * ADV-1330 (scope 4): after a create into [target], list that folder again. My Drive runs
     * [reloadMyDrive] (the listing, as before). A shared folder reloads only while it is still
     * the open folder of Shared with me: Lena may have walked away while her upload ran, and the
     * upload's toasts tell her the rest. My Drive is not listed for a shared create.
     */
    private suspend fun reloadAfterCreate(
        target: CreateTarget,
        quiet: Boolean = false,
        reloadMyDrive: suspend () -> Unit = { if (quiet) quietRefresh() else refresh() },
    ) {
        when (target) {
            is CreateTarget.MyDrive -> reloadMyDrive()
            is CreateTarget.SharedFolder ->
                if (view == DriveView.SHARED && sharedWithMe.currentFolder?.id == target.folderId) sharedWithMe.reopen(quiet)
        }
    }

    /**
     * ADV-1330 (scope 5): a create into [target] failed with [e]. The toast is [prefix] and the
     * reason. My Drive keeps the 1326c rule: a hold found by the one re-poll replaces the toast.
     * In a shared folder the refusal is about the grant, not the person's standing, so there is
     * no re-poll: the toast shows, and a `403` or `404` reads the grants and the folder again
     * ([SharedWithMeStore.recheck]), unless [recheck] is false (an upload batch rechecks once,
     * at its end). A `404` from the create's start there reads [SHARED_FOLDER_GONE], not "That
     * didn't work (404)." A `404` for an upload session's part keeps the usual message.
     * Returns true when [e] was a shared refusal.
     */
    private suspend fun createFailed(target: CreateTarget, e: Throwable, prefix: String, recheck: Boolean = true): Boolean {
        when (target) {
            is CreateTarget.MyDrive -> if (!repollAfterRefusal(e)) toast("$prefix ${e.userFacingMessage()}")
            is CreateTarget.SharedFolder -> {
                toast("$prefix ${if (e.isDestinationGone()) SHARED_FOLDER_GONE else e.userFacingMessage()}")
                if (e.isRefusal()) {
                    if (recheck && view == DriveView.SHARED) sharedWithMe.recheck()
                    return true
                }
            }
        }
        return false
    }

    /**
     * ADV-1330 (review HIGH-1): after a create into a shared folder, wait for its new rows
     * ([SharedWithMeStore.awaitRows]): the sharee listing hides a row until the vault registers
     * it, a moment after the create. My Drive lists its own rows at once and waits for nothing.
     */
    private fun awaitNewRows(target: CreateTarget, expect: List<(List<FileDto>) -> Boolean>) {
        if (target is CreateTarget.SharedFolder && expect.isNotEmpty()) sharedWithMe.awaitRows(target.folderId, expect)
    }

    /**
     * Shows Shared with me. [fromUrl] is the popstate path, which must not push a new history
     * entry. The URL is `/ui/?view=shared`: a query, so no folder name can collide with it.
     */
    fun showSharedWithMe(fromUrl: Boolean = false) {
        clearSelection()
        view = DriveView.SHARED
        if (!fromUrl && !isSharedViewUrl(window.location.search)) {
            window.history.pushState(null, "", "/ui/$SHARED_VIEW_QUERY")
        }
    }

    /** CR-6.3 (D10): shows Shared by me. [fromUrl] is the popstate path, which pushes no history entry. */
    fun showSharedByMe(fromUrl: Boolean = false) {
        clearSelection()
        view = DriveView.SHARED_BY_ME
        if (!fromUrl && !isSharedByMeUrl(window.location.search)) {
            window.history.pushState(null, "", "/ui/$SHARED_BY_ME_VIEW_QUERY")
        }
    }

    /**
     * A `?view=shared` link opened while the share UI is hidden: land on My Drive at the same
     * path, replacing the history entry (not pushing one), so Back does not return to it.
     */
    fun landOnMyDrive() {
        view = DriveView.MY_DRIVE
        window.history.replaceState(null, "", urlPathOf(currentPath))
        MainScope().launch { refresh() }
    }

    private fun urlPathOf(path: String): String = if (path == "/" || path.isBlank()) {
        "/ui/"
    } else {
        "/ui/" + path.split("/").filter { it.isNotBlank() }.joinToString("/") { encodeUriComponent(it) }
    }

    private fun pushUrl(path: String) {
        val urlPath = if (path == "/" || path.isBlank()) {
            "/ui/"
        } else {
            // Percent-encode each segment (symmetric with pathFromLocation()'s decode in
            // Main.kt) so folder names containing #, ?, %, etc. round-trip, instead of
            // relying on the browser to encode the pushState URL (which drops #/? as a
            // fragment/query). ADV-446.
            "/ui/" + path.split("/").filter { it.isNotBlank() }.joinToString("/") { encodeUriComponent(it) }
        }
        if (window.location.pathname != urlPath || window.location.search.isNotEmpty()) {
            window.history.pushState(null, "", urlPath)
        }
    }

    suspend fun prompt(
        title: String,
        message: String? = null,
        inputLabel: String = "",
        placeholder: String = "",
        defaultValue: String = "",
        confirmLabel: String = "OK",
        cancelLabel: String = "Cancel",
        requireText: String? = null,
        destructive: Boolean = false,
        multiline: Boolean = false,
    ): String? {
        val completer = CompletableDeferred<String?>()
        activeModal = ModalRequest.Prompt(
            title = title,
            message = message,
            confirmLabel = confirmLabel,
            cancelLabel = cancelLabel,
            inputLabel = inputLabel,
            placeholder = placeholder,
            defaultValue = defaultValue,
            completer = completer,
            requireText = requireText,
            destructive = destructive,
            multiline = multiline,
        )
        return try {
            completer.await()
        } finally {
            activeModal = null
        }
    }

    suspend fun confirm(
        title: String,
        message: String? = null,
        confirmLabel: String = "OK",
        cancelLabel: String = "Cancel",
        destructive: Boolean = false,
    ): Boolean {
        val completer = CompletableDeferred<Boolean>()
        activeModal = ModalRequest.Confirm(
            title = title,
            message = message,
            confirmLabel = confirmLabel,
            cancelLabel = cancelLabel,
            destructive = destructive,
            completer = completer,
        )
        return try {
            completer.await()
        } finally {
            activeModal = null
        }
    }

    /** Called by the Modal component when the user clicks an action or the backdrop. */
    fun resolveModal(confirmed: Boolean, value: String?) {
        when (val modal = activeModal) {
            is ModalRequest.Confirm -> modal.completer.complete(confirmed)
            is ModalRequest.Prompt -> modal.completer.complete(if (confirmed) value ?: "" else null)
            // PullFromUrl and CuratedRun never route through here — Modal()'s own
            // submit()/cancel() complete their completer directly (each needs its own
            // typed result, not a raw String?).
            is ModalRequest.PullFromUrl -> Unit
            is ModalRequest.CuratedRun -> Unit
            // Params never routes through here either — Modal()'s own submit()/cancel()
            // completes its completer directly with the collected map (or null).
            is ModalRequest.Params -> Unit
            // ADV-1529: completes its own typed completer too.
            is ModalRequest.ExtractTo -> Unit
            null -> Unit
        }
    }

    /**
     * Small generic form dialog: collects [params] (TEXT/SELECT) into a key→value map.
     * Used by the archive compress action (spec §6: archive name + format). Returns
     * null if the user cancelled. Same idiom as [confirm]/[prompt]/[collectOpcRun].
     */
    suspend fun collectParams(
        title: String,
        message: String? = null,
        confirmLabel: String = "OK",
        cancelLabel: String = "Cancel",
        params: List<FormField>,
    ): Map<String, String>? {
        val completer = CompletableDeferred<Map<String, String>?>()
        activeModal = ModalRequest.Params(title, message, confirmLabel, cancelLabel, params, completer)
        return try {
            completer.await()
        } finally {
            activeModal = null
        }
    }

    /**
     * ADV-669 — run the two-step OPC wizard over [catalog] and return **which**
     * opinionated use-case the scientist chose plus their file picks, or null if they
     * cancelled at any point. Step 1 is the card list; step 2 is the file/folder picker
     * for the chosen OPC's declared `file-path`/`file-set` inputs (D4).
     *
     * [currentPath]/[files] — the folder already on screen, at the moment the picker
     * opens — are handed to the modal so it can pre-select obvious candidates
     * (reviewer feedback: don't open on "No files selected" when the folder plainly
     * has the right files). This is a snapshot, not a live reference: the modal never
     * re-lists or expands anything server-side (D21 still holds) and never sees a
     * folder the scientist isn't already browsing under their own session.
     *
     * This collects; it does not submit. The caller turns the result into a
     * `CuratedRunRequest` via [buildCuratedRunRequest] and posts it to
     * `POST /api/pipeline/curated/run`.
     */
    suspend fun collectOpcRun(catalog: List<CuratedWorkflowSpec>): OpcRunSelection? {
        val completer = CompletableDeferred<OpcRunSelection?>()
        // ADV-1330 (scope 9): in a shared folder, the wizard opens on the shared trail and its
        // rows, and browses by folder id. Else My Drive, as before.
        val trail = sharedWithMe.trail
        activeModal = if (view == DriveView.SHARED && trail.isNotEmpty()) {
            ModalRequest.CuratedRun(catalog, PickerSource.Shared(trail), sharedWithMe.items, completer)
        } else {
            ModalRequest.CuratedRun(catalog, PickerSource.Own(currentPath), files, completer)
        }
        return try {
            completer.await()
        } finally {
            activeModal = null
        }
    }

    /** Shows a toast and returns its id, so callers can dismiss it early (e.g. a
     *  progress toast superseded by its completion toast). */
    fun toast(
        message: String,
        level: ToastLevel = ToastLevel.ERROR,
        // An ERROR is a diagnostic the user has to read and act on — a failed pull names a
        // bucket and an object path — and 4s is not enough to notice, read and parse one
        // (long enough to miss entirely if you glance away). Confirmations stay brief.
        durationMs: Long = if (level == ToastLevel.ERROR) ERROR_TOAST_MS else DEFAULT_TOAST_MS,
    ): Long {
        val id = ++nextToastId
        toasts.add(ToastItem(id, message, level))
        // Bulk operations report per item (a 20-file upload can raise 20 upload-failure
        // toasts), so holding each for the longer ERROR duration would bury the page. Keep
        // only the most recent few; each still self-expires on its own timer.
        while (toasts.size > MAX_VISIBLE_TOASTS) toasts.removeAt(0)
        MainScope().launch {
            delay(durationMs)
            toasts.removeAll { it.id == id }
        }
        return id
    }

    fun dismissToast(id: Long) {
        toasts.removeAll { it.id == id }
    }

    fun upload(file: File) {
        val target = createTarget ?: return
        uploadAll(listOf(FileToUpload(file, null)), target)
    }

    /**
     * Upload a list of files. Each file gets its own UploadOrchestrator run so
     * folder uploads work transparently. Files run with bounded concurrency
     * (spec §6 `perFileConcurrency`); part-level concurrency is multiplied on
     * top inside each orchestrator. Each in-flight file gets its own progress
     * row in [activeUploads]; the header counter tracks "N of M completed".
     *
     * [target] is pinned by the caller at the click that opened the file picker (ADV-1330,
     * fresh-eyes L-1), as New folder, Pull and Run workflow pin theirs: the picker stays open
     * while the person may still move. Reading the live `currentPath` per file meant navigating
     * into a subfolder mid-upload landed later files there → runaway nesting
     * (binned_outputs/binned_outputs/…). [upload] is the test seam for one file; null is the
     * production [uploadOne]. Returns the batch's job, or null for no items.
     */
    fun uploadAll(
        items: List<FileToUpload>,
        target: CreateTarget,
        upload: (suspend (CreateTarget, FileToUpload, (UploadOrchestrator.Progress) -> Unit) -> FileDto)? = null,
    ): Job? {
        if (items.isEmpty()) return null
        val backend = authConfig?.storageBackend
        // Each file starts with its fair share of the totalPutBudget. When sibling
        // files finish, rebalanceBudgets() grows the survivors' part-PUT budgets so
        // freed PUT slots are reused immediately instead of sitting idle.
        val parallelFiles = minOf(items.size, perFileConcurrency)
        val initialPartBudget = maxOf(1, totalPutBudget / parallelFiles)
        // Detect folder uploads — every item carries `relativePath` starting with
        // the folder name; the header switches to "Uploading folder \"name\"".
        val folderName = items.firstOrNull()?.relativePath
            ?.substringBefore('/')?.takeIf { it.isNotBlank() }
        console.log("[drive] uploadAll start: ${items.size} files, $parallelFiles parallel, initialPartBudget=$initialPartBudget, folder=${folderName ?: "<none>"}")
        return MainScope().launch {
            installBeforeUnloadWarning()
            try {
                uploadBatch(target, items, folderName, upload ?: { t, item, onProgress ->
                    uploadOne(t, item, backend, initialPartBudget, onProgress)
                })
            } finally {
                removeBeforeUnloadWarning()
            }
        }
    }

    /**
     * Runs one batch of [items] into [target], pinned at the batch's start, with bounded
     * concurrency. [upload] moves one file (the orchestrator in production, [uploadOne]) and is
     * the test seam. ADV-1330: in a shared folder each finished file reopens the folder quietly
     * and waits for its row. A refusal reads the grants again, once per batch, not once per file.
     */
    internal suspend fun uploadBatch(
        target: CreateTarget,
        items: List<FileToUpload>,
        folderName: String? = null,
        upload: suspend (CreateTarget, FileToUpload, (UploadOrchestrator.Progress) -> Unit) -> FileDto,
    ) {
        inFlightUploads = items.size
        completedUploads = 0
        currentUploadFolderName = folderName
        try {
            val refused = coroutineScope {
                val sem = kotlinx.coroutines.sync.Semaphore(perFileConcurrency)
                items.map { item -> async { sem.withPermit { runOneUpload(item, target, upload) } } }.awaitAll().any { it }
            }
            console.log("[drive] uploadAll complete: refreshing file list")
            // A shared folder keeps its rows on screen at the end too. My Drive lists as before.
            if (refused && view == DriveView.SHARED) sharedWithMe.recheck()
            else reloadAfterCreate(target, quiet = target is CreateTarget.SharedFolder)
        } finally {
            inFlightUploads = 0
            completedUploads = 0
            currentUploadFolderName = null
            activeUploads.clear()
        }
    }

    /** Orchestrators currently uploading (S3 backend only). Used by rebalanceBudgets()
     *  to grow surviving orchestrators' part-PUT budgets as siblings finish. */
    private val activeOrchestrators = mutableListOf<UploadOrchestrator>()

    /** Uploads one file into [target]. True when a shared folder refused it (ADV-1330 scope 5). */
    private suspend fun runOneUpload(
        item: FileToUpload,
        target: CreateTarget,
        upload: suspend (CreateTarget, FileToUpload, (UploadOrchestrator.Progress) -> Unit) -> FileDto,
    ): Boolean {
        val uploadId = newUploadProgressId()
        val declaredSize = item.file.size.toString().toLongOrNull() ?: 0L
        console.log("[drive] upload[$uploadId] start: ${item.file.name} ($declaredSize bytes)")
        putUploadProgress(initialProgressRow(uploadId, item.file.name, declaredSize))
        return try {
            val row = upload(target, item) { p -> putUploadProgress(progressRow(uploadId, item.file.name, p)) }
            console.log("[drive] upload[$uploadId] done: ${item.file.name}")
            // Incremental refresh so the newly committed file (and the destination
            // folder, materialized by ensureFolderChain on first commit) appears
            // in the table immediately instead of waiting for the batch to finish.
            reloadAfterCreate(target, quiet = true)
            awaitNewRows(target, listOf(uploadedRowListed(row, item.relativePath)))
            false
        } catch (e: Throwable) {
            console.error("[drive] upload[$uploadId] failed: ${item.file.name}", e)
            // ADV-1326c (ruling 6): a hold found after the refusal replaces the toast (My Drive).
            createFailed(target, e, "Upload failed for ${item.file.name}.", recheck = false)
        } finally {
            removeUploadProgress(uploadId)
            completedUploads += 1
        }
    }

    /**
     * The production upload of one file: the orchestrator on the S3 backend, whose part-PUT
     * budget [rebalanceBudgets] shares out, else the legacy local-fs route.
     */
    private suspend fun uploadOne(
        target: CreateTarget,
        item: FileToUpload,
        backend: String?,
        initialPartBudget: Int,
        onProgress: (UploadOrchestrator.Progress) -> Unit,
    ): FileDto {
        if (backend != "s3") {
            // The legacy local-fs route takes a path only. A shared folder exists only on
            // the S3 backend, so no shared upload comes here.
            val path = (target as? CreateTarget.MyDrive)?.path
                ?: error("A shared folder takes uploads on the S3 storage backend only.")
            return FilesApi.uploadFile(path, item.file, item.relativePath)
        }
        val orchestrator = UploadOrchestrator(initialPartBudget = initialPartBudget, store = resumableStore)
        activeOrchestrators.add(orchestrator)
        try {
            return orchestrator.upload(target = target, file = item.file, relativePath = item.relativePath, onProgress = onProgress)
        } finally {
            activeOrchestrators.remove(orchestrator)
            rebalanceBudgets()
        }
    }

    /**
     * Recompute the per-file part-PUT budget across surviving orchestrators and
     * grow each one to its new share. Called when an orchestrator finishes
     * (success or failure) so the freed PUT slots immediately become available
     * to the files still uploading.
     */
    private fun rebalanceBudgets() {
        val remaining = activeOrchestrators.size
        if (remaining == 0) return
        val fairShare = maxOf(1, totalPutBudget / remaining)
        activeOrchestrators.forEach { orch ->
            val delta = fairShare - orch.currentPartBudget()
            if (delta > 0) {
                console.log("[drive] rebalance: +$delta -> budget=$fairShare for one orchestrator")
                orch.growBudget(delta)
            }
        }
    }

    /** Per-file concurrency for folder uploads. Spec §6 default range 4-8. */
    private val perFileConcurrency: Int = 4

    /** Total PUT-budget shared across all in-flight files.
     *
     *  Empirical observations against cwobject.com (http/1.1):
     *  - budget=1 stays at 38 KiB/s — a fresh lone socket never ramps. Likely
     *    server-side flow control treats single streams as background traffic.
     *  - budget=6 reaches 75 MiB/s on the "winning" socket once cwnd ramps,
     *    but only after the first PUT completes. The orchestrator's pre-warm
     *    (one sequential PUT before the parallel batch) gets us there fast. */
    private val totalPutBudget: Int = 6

    private var beforeUnloadHandler: ((org.w3c.dom.events.Event) -> dynamic)? = null

    private fun installBeforeUnloadWarning() {
        // No window in a Node test.
        if (js("typeof window") == "undefined") return
        val msg = "Uploads are still running."
        val handler: (org.w3c.dom.events.Event) -> dynamic = { event ->
            // Modern browsers ignore the custom string, but setting returnValue triggers
            // the native "Leave site?" confirm. preventDefault() is the spec-compliant signal.
            event.preventDefault()
            event.asDynamic().returnValue = msg
            msg
        }
        beforeUnloadHandler = handler
        window.addEventListener("beforeunload", handler)
    }

    private fun removeBeforeUnloadWarning() {
        if (js("typeof window") == "undefined") return
        beforeUnloadHandler?.let { window.removeEventListener("beforeunload", it) }
        beforeUnloadHandler = null
    }

    data class FileToUpload(val file: File, val relativePath: String?)

    /**
     * [target] is pinned by the caller at the click, before the name prompt, as Pull from URL and
     * Run workflow pin theirs (ADV-1330).
     */
    fun createFolder(name: String, target: CreateTarget) {
        if (name.isBlank()) return
        MainScope().launch { createFolderIn(target, name) }
    }

    /**
     * ADV-1330 (scope 3 to 5): creates the folder [name] in [target] and reloads [target]. [create]
     * and [reloadMyDrive] are seams with production defaults (`FilesApi` and [refresh]).
     */
    internal suspend fun createFolderIn(
        target: CreateTarget,
        name: String,
        create: suspend (CreateTarget, String) -> FileDto = { t, n -> FilesApi.createFolder(t, n) },
        reloadMyDrive: suspend () -> Unit = { refresh() },
    ) {
        try {
            val row = create(target, name.trim())
            reloadAfterCreate(target, reloadMyDrive = reloadMyDrive)
            awaitNewRows(target, listOf(rowListed(row.id)))
        } catch (e: Throwable) {
            createFailed(target, e, "Could not create folder.")
        }
    }

    /**
     * Show the Pull-from-URL modal (URL list + the adaptive s3 credential UI, spec §5)
     * and, on a real submit, POST /api/pull. Pinned to [createTarget] at call time
     * (matches uploadAll): My Drive's folder, or (ADV-1330) the open shared folder.
     * Backend errors (SSRF/over-limit/bad keys) surface as a toast; a successful submit
     * refreshes so the pulled rows (PENDING with pullProgress) appear and the poll loop
     * drives them to READY.
     */
    fun pullFromUrls() {
        val target = createTarget ?: return
        MainScope().launch {
            val completer = CompletableDeferred<PullSubmission?>()
            activeModal = ModalRequest.PullFromUrl(completer)
            val submission = try {
                completer.await()
            } finally {
                activeModal = null
            }
            if (submission == null || submission.urls.isEmpty()) return@launch
            submitPull(target, submission)
        }
    }

    /**
     * POST one [submission] into [target] and reload [target]. [pull] and [reloadMyDrive] are
     * seams with production defaults (`FilesApi` and [refresh]), following [submitOpcRun].
     */
    internal suspend fun submitPull(
        target: CreateTarget,
        submission: PullSubmission,
        pull: suspend (CreateTarget, List<String>, List<S3Credential>) -> PullResponse =
            { t, urls, creds -> FilesApi.pull(t, urls, creds) },
        reloadMyDrive: suspend () -> Unit = { refresh() },
    ) {
        // Immediate feedback: the POST vets + creates a row per URL server-side (a few
        // seconds for a big list), during which the modal has already closed.
        pullSubmitting = true
        setBusyCursor(true)
        toast(
            "Starting ${submission.urls.size} download${if (submission.urls.size == 1) "" else "s"}…",
            ToastLevel.INFO,
        )
        try {
            val res = pull(target, submission.urls, submission.s3Credentials)
            toast(
                "Started ${res.fileIds.size} download${if (res.fileIds.size == 1) "" else "s"}",
                ToastLevel.SUCCESS,
            )
            reloadAfterCreate(target, reloadMyDrive = reloadMyDrive)
            awaitNewRows(target, res.fileIds.map(::rowListed))
        } catch (e: Throwable) {
            createFailed(target, e, "Pull failed.")
            console.error("[drive] pullFromUrls error:", e)
        } finally {
            pullSubmitting = false
            setBusyCursor(false)
        }
    }

    /** The page's wait cursor. No document in a Node test, so it is skipped there. */
    private fun setBusyCursor(busy: Boolean) {
        if (js("typeof document") == "undefined") return
        document.body?.let { it.style.cursor = if (busy) "progress" else "" }
    }

    /**
     * Extract [file]. An ARCHIVE expands into a NEW sibling folder named after it
     * (spec D8) — never "extract here". A plain `.gz` holds one member, so it
     * DECOMPRESSES into a single sibling file instead, and the dialog says so:
     * promising a folder for a one-file result would misdescribe what is about to
     * happen. [extractPrompt] owns that wording.
     *
     * Confirms first (an archive job can create a lot of rows), then enqueues; the
     * backend creates the destination row immediately, so a refresh is enough to show
     * it with a progress bar. Progress rides on FileDto.archiveProgress either way.
     */
    fun extract(
        file: FileDto,
        request: suspend (String, CreateTarget?) -> ExtractResponse = { id, to -> FilesApi.extract(id, to) },
        besideAllowed: suspend (String) -> Boolean = { FilesApi.extractDestinationAllowed(it) },
    ): Job? {
        // ADV-1330 (Tim's rig screens): pinned at the click. In a shared folder the new folder
        // goes beside the archive, in that shared folder. The top level has none.
        // ADV-1529: the dialog offers beside the archive when the server allows it, and else a
        // folder the person picks. A sharee of a single file has no `upload` beside it.
        val beside = createTarget
        return MainScope().launch {
            // Marked in flight BEFORE the dialog opens, not after it is confirmed: the
            // 📂 button stays live while the modal is up, so a second click would open
            // a second dialog whose `finally` clears the flag out from under the first
            // — stranding a spinner (or queueing a duplicate extract).
            extractingIds = extractingIds + file.id
            try {
                val allowed = try {
                    beside != null && besideAllowed(file.id)
                } catch (e: Throwable) {
                    // The archive itself is refused (no `download`, gone) or the check failed.
                    toast("${extractPrompt(file.displayName).failureNoun} failed. ${e.userFacingMessage()}")
                    return@launch
                }
                val choice = collectExtractDestination(file, beside, allowed) ?: return@launch
                extractIn(choice.target, file, request, chosen = !choice.beside)
            } finally {
                extractingIds = extractingIds - file.id
            }
        }
    }

    /**
     * ADV-1529: opens the extract dialog for [file]. [beside] is the archive's own folder, and
     * [besideAllowed] is the server's answer for it. Returns the destination, or null if the
     * person cancelled. Same idiom as [confirm].
     */
    suspend fun collectExtractDestination(file: FileDto, beside: CreateTarget?, besideAllowed: Boolean): ExtractChoice? {
        val prompt = extractPrompt(file.displayName)
        val completer = CompletableDeferred<ExtractChoice?>()
        // The archive's own folder as a place the browser can stand in: My Drive's path, or the
        // open shared folder with its trail (so Back walks up it).
        val besideAt = when (beside) {
            is CreateTarget.MyDrive -> ExtractPickerAt.Own(beside.path)
            is CreateTarget.SharedFolder -> sharedWithMe.trail.takeIf { it.isNotEmpty() }?.let { ExtractPickerAt.Shared(it) }
            null -> null
        }
        activeModal = ModalRequest.ExtractTo(
            title = "${prompt.confirmLabel} \"${file.displayName}\"",
            confirmLabel = prompt.confirmLabel,
            file = file,
            besideAt = besideAt,
            besideAllowed = besideAllowed && besideAt != null,
            completer = completer,
        )
        return try {
            completer.await()
        } finally {
            activeModal = null
        }
    }

    /**
     * Enqueues the extract of [file] into [target] and reloads [target]. [chosen] is false for
     * the archive's own folder (the request names no destination), and true for a folder the
     * person picked (ADV-1529). A shared folder waits for the new destination row, and a refusal
     * takes the shared path ([createFailed]). [request] and [reloadMyDrive] are seams with
     * production defaults.
     */
    internal suspend fun extractIn(
        target: CreateTarget,
        file: FileDto,
        request: suspend (String, CreateTarget?) -> ExtractResponse = { id, to -> FilesApi.extract(id, to) },
        reloadMyDrive: suspend () -> Unit = { refresh() },
        chosen: Boolean = false,
    ) {
        val prompt = extractPrompt(file.displayName)
        try {
            val res = request(file.id, target.takeIf { chosen })
            val into = if (chosen) " into \"${destinationName(target)}\"" else ""
            toast("${prompt.progressVerb} \"${file.displayName}\"$into…", ToastLevel.INFO)
            reloadAfterCreate(target, reloadMyDrive = reloadMyDrive)
            awaitNewRows(target, listOf(rowListed(res.destFolderId)))
        } catch (e: Throwable) {
            createFailed(target, e, "${prompt.failureNoun} failed.")
            console.error("[drive] extract error:", e)
        }
    }

    /**
     * Compress the current selection into one archive in the current folder (spec D9).
     * Strictly additive — the originals are kept (D4). The dialog collects the archive
     * name (prefilled archive-YYYY-MM-DD) and the format; the name is never rejected for
     * collision, the backend suffixes it. Clears the selection once the job is enqueued.
     */
    fun compressSelection(
        compress: suspend (List<String>, String, String) -> CompressResponse = { i, n, f -> FilesApi.compress(i, n, f) },
    ): Job? {
        // Tim (2026-09-28): the selection of the list on screen, pinned at the click with its
        // destination: My Drive's folder, or the open shared folder. [compress] is the seam.
        val target = createTarget ?: return null
        val rows = selectionRows.filter { it.id in selectionIds }
        val ids = rows.map { it.id }
        if (ids.isEmpty()) return null
        return MainScope().launch {
            // Marked submitting BEFORE the dialog opens (see [extract]): the Compress
            // button stays live while the modal is up, and a second dialog's `finally`
            // would clear the flag out from under the first.
            compressSubmitting = true
            try {
                val values = collectParams(
                    title = "Compress ${ids.size} item${if (ids.size == 1) "" else "s"}",
                    message = "Creates one archive in this folder. The originals are kept.",
                    confirmLabel = "Compress",
                    cancelLabel = "Cancel",
                    params = listOf(
                        FormField(
                            key = "name",
                            label = "Archive name",
                            type = FormFieldType.TEXT,
                            defaultValue = suggestedArchiveName(rows),
                        ),
                        FormField(
                            key = "format",
                            label = "Format",
                            type = FormFieldType.SELECT,
                            options = ARCHIVE_FORMATS.map { FormFieldOption(it, it) },
                        ),
                    ),
                ) ?: return@launch
                val name = values["name"]?.trim().orEmpty()
                if (name.isEmpty()) return@launch
                val format = values["format"] ?: ARCHIVE_FORMATS.first()
                setBusyCursor(true)
                compressIn(target, ids, name, format, compress)
            } finally {
                compressSubmitting = false
                setBusyCursor(false)
            }
        }
    }

    /**
     * Enqueues the compress of [ids] into one archive in [target] and reloads [target]. The
     * selection of [target]'s list clears once the job is enqueued. A shared folder waits for
     * the archive's row (`outputFileId`), and a refusal takes the shared path ([createFailed]),
     * keeping the selection. [compress] and [reloadMyDrive] are seams with production defaults.
     */
    internal suspend fun compressIn(
        target: CreateTarget,
        ids: List<String>,
        name: String,
        format: String,
        compress: suspend (List<String>, String, String) -> CompressResponse = { i, n, f -> FilesApi.compress(i, n, f) },
        reloadMyDrive: suspend () -> Unit = { refresh() },
    ) {
        try {
            val res = compress(ids, name, format)
            toast("Compressing ${ids.size} item${if (ids.size == 1) "" else "s"}…", ToastLevel.INFO)
            clearSelectionOf(target)
            reloadAfterCreate(target, reloadMyDrive = reloadMyDrive)
            awaitNewRows(target, listOf(rowListed(res.outputFileId)))
        } catch (e: Throwable) {
            createFailed(target, e, "Compress failed.")
            console.error("[drive] compressSelection error:", e)
        }
    }

    /**
     * ADV-669 — run the two-step OPC wizard over [catalog] (step 1 the card list, step 2
     * the file picker) and submit whatever the scientist chose.
     *
     * Deliberately NOT flag-gated on a new `/api/config` field: when
     * `drive.pipeline.curated-workflows-enabled` is off the backend answers a named 400
     * ("curated workflows are not currently enabled") which the failure toast surfaces
     * verbatim — a visible, explanatory failure beats an invisible button.
     *
     * Lives in the store rather than in the `Header` composable that used to own it,
     * for the same two reasons [pullFromUrls] does: the submit has to refresh (see
     * [submitOpcRun]), and only a store method can be unit-tested.
     */
    fun runOpc() {
        // ADV-1330 (scope 9): pinned at the click, like an upload. The top level of Shared with
        // me has no folder to run in.
        val target = createTarget ?: return
        MainScope().launch { runOpcIn(target) }
    }

    /**
     * The wizard's steps for a run in [target]. [loadCatalog], [collect] and [submit] are seams
     * with production defaults. If the view moved while the catalog loaded, a shared run's wizard
     * does not open: it would open on a folder the click did not mean. A My Drive run opens as it
     * always did.
     */
    internal suspend fun runOpcIn(
        target: CreateTarget,
        loadCatalog: suspend () -> List<CuratedWorkflowSpec>? = { loadOpcCatalog() },
        collect: suspend (List<CuratedWorkflowSpec>) -> OpcRunSelection? = { collectOpcRun(it) },
        submit: suspend (OpcRunSelection) -> Unit = { submitOpcRun(it, target) },
    ) {
        // The flag covers ONLY the fetch. Once the wizard is open the modal is its own
        // feedback, and leaving the button spinning behind it would suggest something
        // is still happening when nothing is.
        opcCatalogLoading = true
        val catalog = try {
            loadCatalog()
        } finally {
            opcCatalogLoading = false
        } ?: return
        // Only a shared run is dropped: My Drive keeps its old behaviour, and the wizard opens
        // on the folder now shown (fresh-eyes L-4).
        if (target is CreateTarget.SharedFolder && createTarget != target) return
        val selection = collect(catalog) ?: return
        submit(selection)
    }

    /**
     * Fetch the catalog from wisp's registry, or null when there is nothing to offer.
     *
     * Null (rather than an empty list) so [runOpc] can bail before opening a wizard onto
     * an empty card list — a modal with no choices is worse than a toast saying why.
     * Both reasons resolve to the same message on purpose: whether curated is disabled
     * for this env or wisp is unreachable, the scientist's situation is identical and
     * neither is theirs to fix.
     *
     * There is deliberately NO fallback to a baked-in list. Rendering a workflow that
     * might not be registered is exactly how a submit earns a 202 and a run_id and then
     * never launches — see the note in `OpcCatalog.kt`.
     *
     * [fetch] is a seam with a production default, following [submitOpcRun].
     */
    private companion object {
        /**
         * What a scientist reads when the workflow registry cannot be reached — from
         * either side of the hop, since the two are the same situation to them.
         *
         * No service name, no status code, no exception class: none of it is theirs to act
         * on, and a technical string reads as "something is broken, possibly by me". It
         * says what is true (temporary), what to do (wait, retry), and what to do if that
         * fails (tell someone) — which is the whole of the useful information.
         */
        const val CATALOG_UNAVAILABLE =
            "Workflows are temporarily unavailable. Please try again in a few minutes — " +
                "if it keeps happening, let support know."
    }

    internal suspend fun loadOpcCatalog(
        fetch: suspend () -> CuratedCatalogResponse = { FilesApi.fetchCuratedCatalog() },
    ): List<CuratedWorkflowSpec>? {
        val response = try {
            fetch()
        } catch (e: Throwable) {
            // DataHub itself did not answer. Deliberately NOT `e.message`: that put
            // "TypeError: Failed to fetch" and bare status text in front of a scientist,
            // which names nothing they can act on. The cause is in the browser console
            // and the server log, where someone who can act on it will look.
            console.error("[drive] workflow catalog fetch failed:", e)
            toast(CATALOG_UNAVAILABLE, ToastLevel.ERROR)
            return null
        }
        // An empty list is the answer to three different questions, and only one of them
        // is a fault. Answering all three with the same calm sentence is what let a
        // registry outage read as a normal quiet day.
        when (response.status) {
            CuratedCatalogStatus.UNAVAILABLE -> {
                toast(CATALOG_UNAVAILABLE, ToastLevel.ERROR)
                return null
            }
            CuratedCatalogStatus.DISABLED -> {
                toast("Running workflows isn't switched on for this account.", ToastLevel.INFO)
                return null
            }
            CuratedCatalogStatus.AVAILABLE -> Unit
        }
        val specs = response.workflows.toSpecs()
        if (specs.isEmpty()) {
            // Reachable and genuinely empty — a fact, not a fault.
            toast("There are no workflows available to run right now.", ToastLevel.INFO)
            return null
        }
        return specs
    }

    /**
     * POST one collected [selection] to `/api/pipeline/curated/run` and, on success,
     * re-list the folder. Returns the minted job id, or null if the submit was rejected.
     *
     * **The refresh is the entire feedback path — do not remove it.** The run is only
     * visible as an overlay on the anchor file's row, which the listing endpoint supplies:
     * no re-list means the row never flips to [Status.PROCESSING], which means
     * [schedulePollIfProcessing] is never armed, which means the run's eventual outcome —
     * FAILED with the backend's rejection reason in `pipelineProgress.error` — never
     * arrives either. The scientist gets a toast and then silence, forever. Same
     * reasoning as [pullFromUrls]' refresh.
     *
     * [submit] and [reload] are seams with production defaults (`FilesApi` and [refresh]),
     * following [walkFolder]'s `listChildren`: they let a test pin the submit→refresh
     * contract without fetch or a timer.
     *
     * ADV-1330 (scope 9): [target] is where the wizard opened. In a shared folder the re-list
     * is that shared folder's (the anchor row shows there, not in My Drive), and a refusal
     * reads the grants again with no admission re-poll ([createFailed]). [reload] is My Drive's.
     */
    internal suspend fun submitOpcRun(
        selection: OpcRunSelection,
        target: CreateTarget = CreateTarget.MyDrive(currentPath),
        submit: suspend (CuratedRunRequest) -> String =
            { FilesApi.runCuratedWorkflow(it, shared = target is CreateTarget.SharedFolder) },
        reload: suspend () -> Unit = { refresh() },
    ): String? {
        val req = buildCuratedRunRequest(selection.opc, selection.picksByInput, selection.knobValues)
        return try {
            val jobId = submit(req)
            toast("Started ${selection.opc.title} — job $jobId", ToastLevel.INFO)
            reloadAfterCreate(target, reloadMyDrive = reload)
            jobId
        } catch (e: Throwable) {
            createFailed(target, e, "Could not start ${selection.opc.title}.")
            console.error("[drive] submitOpcRun error:", e)
            null
        }
    }

    /**
     * Deletes [file] the way of the view it was clicked in, [from] (pinned by [confirmDelete]
     * before its prompt). A Shared with me delete goes to [deleteShared], with [remove] as its
     * seam, and returns its job. My Drive's returns null.
     */
    fun delete(
        file: FileDto,
        from: DriveView = view,
        remove: suspend (String) -> DeleteResult = { FilesApi.deleteFile(it) },
    ): Job? {
        if (from == DriveView.SHARED) return MainScope().launch { deleteShared(file, remove) }
        val isFolder = file.kind == FileKind.FOLDER
        val label = file.displayName
        // Optimistic: drop it from the list immediately so there's instant feedback. A
        // folder answers 202 at once and comes back on the refresh with its delete
        // progress bar (FileDto.deleteProgress) until the background delete removes it.
        files = files.filter { it.id != file.id }
        selectedIds = selectedIds - file.id
        // Progress toast covers the server-side gap; its long duration is just a
        // fallback — every completion path dismisses it before showing its result,
        // so "Deleting…" never lingers past the "Deleted"/failure toast.
        val progressToastId = toast(
            if (isFolder) "Deleting folder \"$label\" and its contents…" else "Deleting \"$label\"…",
            ToastLevel.INFO,
            durationMs = 8000L,
        )
        MainScope().launch {
            try {
                when (FilesApi.deleteFile(file.id)) {
                    DeleteResult.OK -> {
                        dismissToast(progressToastId)
                        refresh()
                        toast("Deleted \"$label\"", ToastLevel.SUCCESS)
                    }
                    DeleteResult.ACCEPTED -> {
                        dismissToast(progressToastId)
                        refresh()
                        toast(deletingInBackground(label), ToastLevel.INFO)
                    }
                    // Already gone server-side — the optimistic removal stands.
                    DeleteResult.NOT_FOUND -> {
                        dismissToast(progressToastId)
                        refresh()
                    }
                }
            } catch (e: Throwable) {
                // The delete didn't take — reconcile (re-adds the row) and report.
                dismissToast(progressToastId)
                toast("Delete failed. ${e.userFacingMessage()}")
                refresh()
            }
        }
        return null
    }

    /**
     * Asks before [file] is deleted: a typed "delete me" for a folder (the delete is recursive
     * and irreversible), a confirm for a file. The view is pinned at the click, before the
     * prompt, as Extract pins its target, so a move while the dialog is open changes nothing.
     * [delete] is the seam with the production default.
     */
    internal suspend fun confirmDelete(
        file: FileDto,
        delete: (FileDto, DriveView) -> Unit = { f, v -> this.delete(f, v) },
    ) {
        val from = view
        val ok = if (file.kind == FileKind.FOLDER) {
            prompt(
                title = "Delete folder?",
                message = "This permanently deletes the folder \"${file.displayName}\" and ALL of " +
                    "its descendants — every file and subfolder inside it. This cannot be undone.",
                inputLabel = "Type \"delete me\" to confirm",
                placeholder = "delete me",
                confirmLabel = "Delete folder",
                requireText = "delete me",
                destructive = true,
            ) != null
        } else {
            confirm(
                title = "Delete file?",
                message = "\"${file.displayName}\" will be removed. This cannot be undone.",
                confirmLabel = "Delete",
                destructive = true,
            )
        }
        if (ok) delete(file, from)
    }

    /**
     * Tim's rig screens: Delete on a Shared with me row (shown only with `delete`, or, ADV-1412,
     * on a row the caller added while holding `upload`, see [SharedWithMeStore.rowActions]). The row goes at once, the server decides, and the list is
     * read again either way, so a refused delete brings the row back. A refusal takes the shared
     * path ([createFailed]), and so does a `404` ([DeleteResult.NOT_FOUND]): the grant is gone,
     * so no success toast, and [SHARED_FOLDER_GONE]. After either, the recheck is the one
     * reload. [remove] is the seam with the production default.
     */
    internal suspend fun deleteShared(
        file: FileDto,
        remove: suspend (String) -> DeleteResult = { FilesApi.deleteFile(it) },
    ) {
        val label = file.displayName
        val open = sharedWithMe.currentFolder
        val target = open?.let { CreateTarget.SharedFolder(it.id, it.displayName) } ?: CreateTarget.SharedFolder(file.id, label)
        sharedWithMe.forget(file.id)
        val progressToastId = toast(
            if (file.kind == FileKind.FOLDER) "Deleting folder \"$label\" and its contents…" else "Deleting \"$label\"…",
            ToastLevel.INFO,
            durationMs = 8000L,
        )
        val refused = try {
            val result = remove(file.id)
            dismissToast(progressToastId)
            when (result) {
                DeleteResult.OK -> {
                    toast("Deleted \"$label\"", ToastLevel.SUCCESS)
                    false
                }
                DeleteResult.ACCEPTED -> {
                    toast(deletingInBackground(label), ToastLevel.INFO)
                    false
                }
                DeleteResult.NOT_FOUND -> {
                    toast("Delete failed. $SHARED_FOLDER_GONE")
                    if (view == DriveView.SHARED) sharedWithMe.recheck()
                    true
                }
            }
        } catch (e: Throwable) {
            dismissToast(progressToastId)
            createFailed(target, e, "Delete failed.")
        }
        // A refusal's recheck already read the folder again. On the roots list it reads nothing,
        // so the roots are read here.
        when {
            sharedWithMe.trail.isEmpty() -> sharedWithMe.load()
            !refused -> sharedWithMe.reopen(quiet = true)
        }
    }

    fun download(file: FileDto) {
        MainScope().launch {
            try {
                FilesApi.download(file)
            } catch (e: Throwable) {
                toast("Download failed. ${e.userFacingMessage()}")
            }
        }
    }

    // ---- selection-bar bulk actions ----------------------------------------

    /**
     * Extract every selected archive — the way users actually reach for it (tick the
     * archives, look at the bar). A no-op for any other selection; [SelectionBar]
     * only offers the button when [extractableSelection] is non-empty.
     *
     * One item delegates to [extract] so the single-archive flow is literally the
     * same code, wording included. A batch gets ONE confirm and then enqueues per
     * archive: each is an independent job, so the backend needs nothing new — extract
     * has always been per-archive, and the queue drains them in turn. A failure to
     * enqueue one is reported and the rest still go, exactly like bulk delete;
     * stopping at the first failure would leave an arbitrary half of the selection
     * queued with no report of which half.
     */
    fun extractSelection(
        request: suspend (String, CreateTarget?) -> ExtractResponse = { id, to -> FilesApi.extract(id, to) },
    ): Job? {
        val targets = extractableSelection(selectionRows, selectionIds)
        if (targets.isEmpty() || bulkBusy) return null
        if (targets.size == 1) return extract(targets.single(), request)
        // Pinned at the click: My Drive's folder, or the open shared folder. [request] is the seam.
        val destination = createTarget ?: return null
        val prompt = extractBatchPrompt(targets.map { it.displayName })
        val ids = targets.map { it.id }.toSet()
        // Both flags set BEFORE the dialog opens — same rule as every other bar
        // action, so a second click cannot open a second dialog whose `finally`
        // clears the flags out from under the first.
        bulkBusy = true
        extractingIds = extractingIds + ids
        return MainScope().launch {
            try {
                val ok = confirm(
                    title = prompt.title,
                    message = prompt.message,
                    confirmLabel = prompt.confirmLabel,
                )
                if (!ok) return@launch
                var enqueued = 0
                var refused = false
                val made = mutableListOf<String>()
                val failures = mutableListOf<String>()
                for (target in targets) {
                    try {
                        made += request(target.id, null).destFolderId
                        enqueued++
                    } catch (e: Throwable) {
                        if (destination is CreateTarget.MyDrive) {
                            // ADV-1326c (ruling 6): a hold found after the refusal ends the batch.
                            if (repollAfterRefusal(e)) return@launch
                        } else if (e.isRefusal()) {
                            refused = true
                        }
                        failures += "${target.displayName}: ${e.userFacingMessage()}"
                        console.error("[drive] batch extract error for ${target.id}:", e)
                    }
                }
                if (failures.isEmpty()) {
                    toast("${prompt.progressVerb} ${targets.size} items…", ToastLevel.INFO)
                } else {
                    toast(
                        "${prompt.failureNoun}ed $enqueued of ${targets.size} enqueued — " +
                            "${failures.size} failed: ${failures.first()}",
                    )
                }
                clearSelectionOf(destination)
                // A shared refusal reads the grants again, once for the batch.
                if (refused && view == DriveView.SHARED) sharedWithMe.recheck() else reloadAfterCreate(destination)
                awaitNewRows(destination, made.map(::rowListed))
            } finally {
                bulkBusy = false
                extractingIds = extractingIds - ids
            }
        }
    }

    /**
     * ADV-1463: runs [remove] for each of [rows], at most [BULK_DELETE_CONCURRENCY] at once, and
     * keeps [bulkDelete] up to date. [remove] returns null for a success, or the reason for a
     * failure. With no failure the pop-up closes [BULK_DELETE_CLOSE_MS] after the last item. With
     * a failure it stays open until [closeBulkDelete].
     *
     * Example: Tim deletes 5 items. 4 start at once, and the fifth starts when the first ends.
     * "Plate QC" fails with "Forbidden", so the pop-up stays open with that reason.
     */
    internal suspend fun runBulkDelete(
        rows: List<FileDto>,
        scope: CoroutineScope,
        remove: suspend (FileDto) -> String?,
    ): BulkDeleteState {
        // The outcome lives here, not in [bulkDelete]: the user may close the pop-up mid-batch,
        // and the summary must still count every result (review, ADV-1463).
        val items = rows.map { BulkDeleteItem(it.id, it.displayName) }.toMutableList()
        val index = rows.withIndex().associate { (i, r) -> r.id to i }
        var open = true
        bulkDelete = BulkDeleteState(items.toList())
        fun mark(id: String, s: BulkItemState, reason: String? = null) {
            val i = index[id] ?: return
            items[i] = items[i].copy(state = s, reason = reason)
            if (open && bulkDelete == null) open = false
            if (open) bulkDelete = BulkDeleteState(items.toList())
        }
        val gate = Semaphore(BULK_DELETE_CONCURRENCY)
        coroutineScope {
            rows.map { row ->
                async {
                    gate.withPermit {
                        mark(row.id, BulkItemState.DELETING)
                        val reason = try { remove(row) } catch (e: CancellationException) { throw e } catch (e: Throwable) { e.userFacingMessage() }
                        if (reason == null) mark(row.id, BulkItemState.DONE) else mark(row.id, BulkItemState.FAILED, reason)
                    }
                }
            }.awaitAll()
        }
        val end = BulkDeleteState(items.toList())
        val shown = bulkDelete
        if (end.failed == 0 && shown != null) {
            scope.launch {
                delay(BULK_DELETE_CLOSE_MS)
                if (bulkDelete === shown) bulkDelete = null
            }
        }
        return end
    }

    /**
     * Delete every selected row. ONE confirmation for the batch, not N — and the
     * typed "delete me" gate whenever a folder is in the selection, matching the
     * per-row folder delete, because a folder delete is recursive and irreversible.
     *
     * Per-item failures are tolerated and summarized rather than aborting the batch:
     * the user asked for all of them, and stopping at the first failure leaves an
     * arbitrary half-done state with no report of which half.
     */
    fun deleteSelection(
        removeShared: suspend (String) -> DeleteResult = { FilesApi.deleteFile(it) },
    ): Job? {
        if (view == DriveView.SHARED) {
            // [removeShared] is the seam of the shared bulk delete.
            val rows = selectionRows.filter { it.id in selectionIds }
            if (rows.isEmpty() || bulkBusy) return null
            bulkBusy = true
            return MainScope().launch {
                try {
                    if (confirmBulkDelete(rows)) deleteSharedSelection(rows, removeShared)
                } finally {
                    bulkBusy = false
                }
            }
        }
        val targets = files.filter { it.id in selectedIds }
        if (targets.isEmpty() || bulkBusy) return null
        // Set BEFORE the dialog opens — same rule as extract/compress, so a second
        // click cannot open a second dialog that clears the flag under the first.
        bulkBusy = true
        return MainScope().launch {
            try {
                if (!confirmBulkDelete(targets)) return@launch

                // Optimistic removal of the whole batch up front, mirroring the per-row
                // delete: a recursive folder delete takes seconds server-side and the
                // final refresh() reconciles anything that did not actually go.
                val ids = targets.map { it.id }.toSet()
                files = files.filter { it.id !in ids }
                // The lambda runs on the single JS thread, so this plain var is safe.
                var inBackground = 0
                val end = runBulkDelete(targets, MainScope()) { target ->
                    // Up to BULK_DELETE_CONCURRENCY at once (ADV-1463). A folder answers at
                    // once (202) and deletes in the background.
                    try {
                        if (FilesApi.deleteFile(target.id) == DeleteResult.ACCEPTED) inBackground++
                        null
                    } catch (e: CancellationException) {
                        throw e
                    } catch (e: Throwable) {
                        console.error("[drive] bulk delete error for ${target.id}:", e)
                        e.userFacingMessage()
                    }
                }
                val deleted = end.total - end.failed
                val firstFailure = end.items.firstOrNull { it.state == BulkItemState.FAILED }
                if (firstFailure == null && inBackground > 0) {
                    toast(
                        "Deleted ${targets.size - inBackground} item${plural(targets.size - inBackground)}; " +
                            "$inBackground folder${plural(inBackground)} still deleting",
                        ToastLevel.INFO,
                    )
                } else if (firstFailure == null) {
                    toast("Deleted ${targets.size} item${plural(targets.size)}", ToastLevel.SUCCESS)
                } else {
                    toast("Deleted $deleted of ${targets.size} — ${end.failed} failed: ${firstFailure.name}: ${firstFailure.reason}")
                }
                clearSelection()
                refresh()
            } finally {
                bulkBusy = false
            }
        }
    }

    /**
     * Download every selected file, one at a time through the same presigned path the
     * row's ⬇ uses. Files only — a folder has no single blob to fetch, and "download
     * a folder" is Compress by design ([SelectionBar] hides the button otherwise).
     *
     * Sequential with a short gap: browsers throttle or silently drop rapid-fire
     * programmatic downloads, and the first one also triggers the permission prompt.
     */
    fun downloadSelection(download: suspend (FileDto) -> Unit = { FilesApi.download(it) }): Job? {
        val targets = selectionRows.filter { it.id in selectionIds && it.kind == FileKind.FILE }
        if (targets.isEmpty() || bulkBusy) return null
        bulkBusy = true
        return MainScope().launch {
            try {
                var failures = 0
                for ((index, target) in targets.withIndex()) {
                    try {
                        download(target)
                    } catch (e: Throwable) {
                        failures++
                        console.error("[drive] bulk download error for ${target.id}:", e)
                    }
                    if (index < targets.lastIndex) delay(BULK_DOWNLOAD_GAP_MS)
                }
                if (failures > 0) {
                    toast("Started ${targets.size - failures} of ${targets.size} downloads — $failures failed")
                } else if (targets.size > 1) {
                    toast("Started ${targets.size} downloads", ToastLevel.INFO)
                }
            } finally {
                bulkBusy = false
            }
        }
    }

    /** The one confirm for a bulk delete: typed "delete me" when a folder is in [rows]. */
    private suspend fun confirmBulkDelete(rows: List<FileDto>): Boolean {
        // The helper, not an inline count: one home for the rule that decides the
        // typed-confirmation gate, so the expression under test and the expression
        // that ships cannot drift (same reason uniqueNameAgainst is shared).
        val folders = selectedFolderCount(rows, rows.map { it.id }.toSet())
        return if (folders > 0) {
            prompt(
                title = "Delete ${rows.size} item${plural(rows.size)}?",
                message = "This permanently deletes ${rows.size} item${plural(rows.size)}, " +
                    "including $folders folder${plural(folders)} and ALL of their descendants — " +
                    "every file and subfolder inside them. This cannot be undone.",
                inputLabel = "Type \"delete me\" to confirm",
                placeholder = "delete me",
                confirmLabel = "Delete ${rows.size} item${plural(rows.size)}",
                requireText = "delete me",
                destructive = true,
            ) != null
        } else {
            confirm(
                title = "Delete ${rows.size} file${plural(rows.size)}?",
                message = "${rows.size} file${plural(rows.size)} will be removed. This cannot be undone.",
                confirmLabel = "Delete",
                destructive = true,
            )
        }
    }

    /**
     * The bar's Delete in a shared folder (shown only when every pick qualifies, see
     * [mayDeleteInFolder]). The rows go at once, each
     * is deleted in turn, and the folder is read again: a refusal or a `404` (the grant is gone)
     * reads the grants again, once for the batch. [remove] is the seam with the production default.
     */
    internal suspend fun deleteSharedSelection(
        rows: List<FileDto>,
        remove: suspend (String) -> DeleteResult = { FilesApi.deleteFile(it) },
    ) {
        val open = sharedWithMe.currentFolder
        rows.forEach { sharedWithMe.forget(it.id) }
        // The lambda runs on the single JS thread (no true parallelism), so this plain
        // var is safe even with up to BULK_DELETE_CONCURRENCY of these in flight.
        var refused = false
        val end = runBulkDelete(rows, MainScope()) { row ->
            try {
                when (remove(row.id)) {
                    DeleteResult.OK, DeleteResult.ACCEPTED -> null
                    DeleteResult.NOT_FOUND -> {
                        refused = true
                        SHARED_FOLDER_GONE
                    }
                }
            } catch (e: CancellationException) {
                throw e
            } catch (e: Throwable) {
                if (e.isRefusal()) refused = true
                console.error("[drive] bulk delete error for ${row.id}:", e)
                e.userFacingMessage()
            }
        }
        val deleted = end.total - end.failed
        val firstFailure = end.items.firstOrNull { it.state == BulkItemState.FAILED }
        if (firstFailure == null) toast("Deleted ${rows.size} item${plural(rows.size)}", ToastLevel.SUCCESS)
        else toast("Deleted $deleted of ${rows.size} — ${end.failed} failed: ${firstFailure.name}: ${firstFailure.reason}")
        sharedWithMe.clearSelection()
        when {
            // The roots list: read the roots again (a recheck reads nothing there).
            open == null -> sharedWithMe.load()
            refused && view == DriveView.SHARED -> sharedWithMe.recheck()
            else -> reloadAfterCreate(CreateTarget.SharedFolder(open.id, open.displayName), quiet = true)
        }
    }
}

/** "s" unless [n] is 1 — keeps the bulk copy readable at the call site. */
private fun plural(n: Int): String = if (n == 1) "" else "s"

/** Gap between programmatic downloads; browsers drop rapid-fire ones. */
private const val BULK_DOWNLOAD_GAP_MS = 400L

/**
 * The selected rows when EVERY one of them is extractable, else empty.
 *
 * Extract is offered for any all-extractable selection, of any size: each archive
 * expands independently into its own destination, so N of them is just N jobs — they
 * drain one at a time per owner (maxConcurrentJobsPerOwner). One item is simply the
 * N=1 case — there is no separate single-item predicate to drift out of step.
 *
 * A MIXED selection returns empty: offering "Extract" over a set that includes a
 * plain .txt would either silently skip rows or need a per-row explanation the bar
 * has no room for. Compress already covers heterogeneous selections.
 *
 * Pure, and built on the same [isExtractable] the row icon uses, so the bar and the
 * row can never disagree about what is extractable — including `.gz`, which the
 * shared extension list brings along for free.
 */
internal fun extractableSelection(files: List<FileDto>, selectedIds: Set<String>): List<FileDto> {
    val selected = files.filter { it.id in selectedIds }
    return if (selected.isNotEmpty() && selected.all { isExtractable(it) }) selected else emptyList()
}

/**
 * True when every selected row is a FILE (and there is at least one). Gates the bulk
 * Download button: a folder has no blob to fetch.
 */
internal fun selectionIsFilesOnly(files: List<FileDto>, selectedIds: Set<String>): Boolean {
    val selected = files.filter { it.id in selectedIds }
    return selected.isNotEmpty() && selected.all { it.kind == FileKind.FILE }
}

/** How many selected rows are folders — decides the typed-confirmation gate. */
internal fun selectedFolderCount(files: List<FileDto>, selectedIds: Set<String>): Int =
    files.count { it.id in selectedIds && it.kind == FileKind.FOLDER }

/**
 * Filename extensions the archive engine can extract. Mirrors the backend's
 * EXTRACT_EXTENSIONS (com.advaita.cloud.drive.archive) — the server re-validates, so
 * this only decides whether to offer the 📂 action. Order matches the backend list
 * verbatim (cosmetic only — [archiveBaseName]'s lookup is itself order-independent,
 * `maxByOrNull { it.length }` exactly like the backend's `extractExtensionOf`, so
 * this list can never silently drift out of "mirrors the backend" by reordering).
 */
internal val EXTRACT_EXTENSIONS = listOf(".zip", ".tar.gz", ".tgz", ".tar", ".gz")

/**
 * Extensions holding ONE compressed member rather than an archive of many. Mirrors
 * the backend's SINGLE_MEMBER_EXTENSIONS. These decide the dialog's wording — a
 * sibling FILE, not a new folder — and nothing else; the server decides the shape.
 */
internal val SINGLE_MEMBER_EXTENSIONS = listOf(".gz")

/** True when [displayName] is a single-member compressed file (a plain `.gz`). */
internal fun isSingleMemberArchive(displayName: String): Boolean {
    val lower = displayName.lowercase()
    // Longest match, so `x.tar.gz` resolves to ".tar.gz" and is NOT single-member.
    val ext = EXTRACT_EXTENSIONS.filter { lower.endsWith(it) }.maxByOrNull { it.length }
    return ext in SINGLE_MEMBER_EXTENSIONS
}

/**
 * The verb for the action on [displayName] — "Extract" for an archive,
 * "Decompress" for a plain `.gz`. Drives the row icon's tooltip and accessible name
 * and the selection bar's button label, so the control a user reads before clicking
 * describes the same thing the confirm dialog then offers.
 */
internal fun extractActionVerb(displayName: String): String =
    if (isSingleMemberArchive(displayName)) "Decompress" else "Extract"

/** The row-icon / bar tooltip for the action on [displayName]. */
internal fun extractActionTip(displayName: String): String =
    if (isSingleMemberArchive(displayName)) {
        "Decompress $displayName into ${archiveBaseName(displayName)}"
    } else {
        "Extract $displayName into a new folder"
    }

/** The bar's tooltip for a whole selection — the batch explanation for N > 1. */
internal fun extractSelectionTip(displayNames: List<String>): String =
    if (displayNames.size == 1) extractActionTip(displayNames.single())
    else extractBatchPrompt(displayNames).message

/** `"name/"` for an archive's destination folder, `"name"` for a decompressed file. */
private fun destPreviewOf(displayName: String): String {
    val base = archiveBaseName(displayName)
    return if (isSingleMemberArchive(displayName)) "\"$base\"" else "\"$base/\""
}

/** Above this many items the confirm names a count instead of listing destinations. */
private const val BATCH_PREVIEW_LIMIT = 3

/** The confirm-dialog wording for one extract, plus the toast nouns that follow it. */
internal data class ExtractPrompt(
    val title: String,
    val message: String,
    val confirmLabel: String,
    val progressVerb: String,
    val failureNoun: String,
)

/**
 * Extract-vs-decompress dialog copy for [displayName].
 *
 * An archive expands into a new folder; a plain `.gz` becomes one sibling file. The
 * dialog has to distinguish them — a user told "into a new folder dataset/" who then
 * finds a single file has been misinformed about an irreversible-feeling action.
 *
 * Pure so the branch is testable without a DOM. The destination-name preview uses
 * [archiveBaseName], the same longest-match stripper the backend names the row with,
 * so the preview cannot disagree with the result.
 */
internal fun extractPrompt(displayName: String): ExtractPrompt {
    val base = archiveBaseName(displayName)
    return if (isSingleMemberArchive(displayName)) {
        ExtractPrompt(
            title = "Decompress file?",
            message = "Decompress \"$displayName\" into \"$base\"? The compressed file is kept.",
            confirmLabel = "Decompress",
            progressVerb = "Decompressing",
            failureNoun = "Decompress",
        )
    } else {
        ExtractPrompt(
            title = "Extract archive?",
            message = "Extract \"$displayName\" into a new folder \"$base/\"? The archive itself is kept.",
            confirmLabel = "Extract",
            progressVerb = "Extracting",
            failureNoun = "Extract",
        )
    }
}

/**
 * ONE confirm for a whole extractable selection.
 *
 * A single item delegates to [extractPrompt], so the familiar wording and its
 * destination preview are unchanged — there is no second copy of it to drift.
 *
 * For a batch the wording follows what the batch actually contains, because a
 * selection may legitimately mix archives and `.gz` files: all archives get "each
 * into its own new folder", all `.gz` get "each alongside its original", and a mixed
 * batch says both rather than picking one and being wrong about half the rows. Up to
 * [BATCH_PREVIEW_LIMIT] destinations are named; beyond that the list would be longer
 * than the dialog, so the count stands on its own.
 */
internal fun extractBatchPrompt(displayNames: List<String>): ExtractPrompt {
    if (displayNames.size == 1) return extractPrompt(displayNames.single())
    val n = displayNames.size
    val preview =
        if (n <= BATCH_PREVIEW_LIMIT) " (" + displayNames.joinToString(", ") { destPreviewOf(it) } + ")" else ""
    val singleMembers = displayNames.count { isSingleMemberArchive(it) }
    return when (singleMembers) {
        n -> ExtractPrompt(
            title = "Decompress $n files?",
            message = "Decompress $n files, each alongside its original$preview? " +
                "The compressed files are kept.",
            confirmLabel = "Decompress",
            progressVerb = "Decompressing",
            failureNoun = "Decompress",
        )
        0 -> ExtractPrompt(
            title = "Extract $n archives?",
            message = "Extract $n archives, each into its own new folder$preview? " +
                "The archives themselves are kept.",
            confirmLabel = "Extract",
            progressVerb = "Extracting",
            failureNoun = "Extract",
        )
        else -> ExtractPrompt(
            title = "Extract $n items?",
            message = "Extract $n selected items$preview? Archives expand into new folders and " +
                "compressed files decompress alongside them. The originals are kept.",
            confirmLabel = "Extract",
            progressVerb = "Extracting",
            failureNoun = "Extract",
        )
    }
}

/** Compress formats offered in the dialog (backend ArchiveFormat constants). zip is
 *  first, which makes it the SELECT's default. */
internal val ARCHIVE_FORMATS = listOf("zip", "tar.gz", "tar")

/**
 * True when [file] is a plain, fully-uploaded file with an extractable extension.
 * Note the drive_file row's DB status "UPLOADED" reaches the SPA as [Status.READY]
 * (FileService.toDto maps processing_status, not the upload status).
 */
internal fun isExtractable(file: FileDto): Boolean =
    file.kind == FileKind.FILE &&
        // The row's own readiness, not the displayed badge: a failed workflow run on a
        // complete tar.gz shows FAILED, and must not hide Extract (the server checks only
        // that the upload finished).
        (file.fileStatus ?: file.status) == Status.READY &&
        EXTRACT_EXTENSIONS.any { file.displayName.lowercase().endsWith(it) }

/**
 * "dataset.tar.gz" → "dataset" — the destination folder name the backend will use,
 * before any collision suffix ("dataset (2)"). Longest-match lookup (not "first
 * matching entry in the list") so ".tar.gz" always wins over ".tar" regardless of
 * [EXTRACT_EXTENSIONS]' declaration order — mirrors the backend's
 * `extractExtensionOf`. A name that IS just the extension (e.g. ".zip") previews
 * "archive/", matching the backend's own `ifBlank { "archive" }` fallback.
 */
internal fun archiveBaseName(displayName: String): String {
    val lower = displayName.lowercase()
    val ext = EXTRACT_EXTENSIONS.filter { lower.endsWith(it) }.maxByOrNull { it.length } ?: return displayName
    return displayName.dropLast(ext.length).ifBlank { "archive" }
}

/**
 * The name to prefill the compress dialog with for [selection].
 *
 * Exactly one item: its own name, which is almost always what the user means —
 * compressing `run_0424/` produces `run_0424.zip`, not `archive-2026-09-24.zip`.
 * A folder keeps its name verbatim (a folder called `my.data` is not "my" with a
 * `.data` extension — the backend's own uniqueName KDoc makes the same point); a
 * file loses its extension, with the longest-match archive suffix stripped whole so
 * `x.tar.gz` gives `x` rather than `x.tar`.
 *
 * Anything else — several items, or a selection whose rows are not in the current
 * listing — keeps the date default: there is no single name to borrow.
 *
 * NO client-side uniqueness. Compressing `run_0424` in place would collide with the
 * folder it came from, and the server already answers that by suffixing
 * (`run_0424 (2).zip`). Duplicating the rule here would only let the two drift.
 */
internal fun suggestedArchiveName(selection: List<FileDto>, now: Date = Date()): String {
    val only = selection.singleOrNull() ?: return defaultArchiveName(now)
    val name = only.displayName
    if (only.kind == FileKind.FOLDER) return name.ifBlank { "archive" }
    // Archive suffixes are multi-part, so they get the longest-match helper the
    // extract preview already uses — including its `ifBlank { "archive" }` for a
    // name that IS just an extension.
    if (EXTRACT_EXTENSIONS.any { name.lowercase().endsWith(it) }) return archiveBaseName(name)
    // Otherwise drop one trailing extension. `dot > 0`, not `>= 0`, mirrors the
    // backend's uniqueName: a dotfile like `.env` is a name, not an extension.
    val dot = name.lastIndexOf('.')
    val stem = if (dot > 0) name.substring(0, dot) else name
    return stem.ifBlank { "archive" }
}

/** `archive-YYYY-MM-DD` from the browser's LOCAL date — "today" as the user sees it. */
internal fun defaultArchiveName(now: Date = Date()): String {
    val month = (now.getMonth() + 1).toString().padStart(2, '0')
    val day = now.getDate().toString().padStart(2, '0')
    return "archive-${now.getFullYear()}-$month-$day"
}

/** A shared search result that the walk to it no longer finds. */
internal const val SEARCH_RESULT_GONE = "That item is no longer shared with you."

/** My Drive's empty folder line, which FileTable shows by default. */
internal const val EMPTY_FOLDER_TEXT = "This folder is empty. Use Upload or New folder to add something."

/** ADV-1330 (review): a bare `404` on a create in a shared folder: the grant went. */
internal const val SHARED_FOLDER_GONE = "This folder is no longer shared with you."

/** The toast for a folder delete the server accepted (`202`): it finishes in the background. */
internal fun deletingInBackground(label: String) =
    "Deleting \"$label\". The folder stays in the list with a progress bar until it is gone."

/** True once the row [id] is in a listing. */
private fun rowListed(id: String): (List<FileDto>) -> Boolean = { rows -> rows.any { it.id == id } }

/**
 * True once an uploaded file shows in the folder it went into: the file itself, or, for Upload
 * folder (a [relativePath] with a folder in it), the top folder of its path.
 */
private fun uploadedRowListed(row: FileDto, relativePath: String?): (List<FileDto>) -> Boolean {
    val top = relativePath?.takeIf { '/' in it }?.substringBefore('/')?.takeIf { it.isNotBlank() }
        ?: return rowListed(row.id)
    return { rows -> rows.any { it.kind == FileKind.FOLDER && it.displayName == top } }
}

/**
 * The selection bar's actions for one selection ([selectionActions]). [compressTip] non-null
 * shows Compress disabled, with that tip (the roots list of Shared with me).
 */
data class SelectionActions(
    val extract: Boolean,
    val compress: Boolean,
    val download: Boolean,
    val delete: Boolean,
    val compressTip: String? = null,
)

/** Compress on the roots list of Shared with me, which the backend refuses for a sharee. */
const val ROOTS_COMPRESS_TIP = "Open a shared folder to compress files in it."

/**
 * Tim (2026-09-28): the selection bar's actions on the roots list of Shared with me, where each
 * picked root has its own grants ([capsOf]). Only what the backend accepts there:
 *
 * - Download: every pick a file, and every picked root holds `download` (`/files/{id}/content`).
 * - Delete: every picked root holds `delete` (`DELETE /api/files/{id}`). ADV-1412: a root that
 *   is [FileDto.mine] does not count, because a root is another person's item shared with you.
 * - Compress: off, with [ROOTS_COMPRESS_TIP]. `ArchiveGate` writes the archive into the first
 *   input's folder, which for a root is the owner's own folder or root, refused for a sharee.
 * - Extract: never, for the same reason.
 *
 * Leave stays a row action.
 */
fun rootsSelectionActions(rows: List<FileDto>, ids: Set<String>, capsOf: (FileDto) -> List<String>): SelectionActions {
    val picked = rows.filter { it.id in ids }
    val all = { cap: String -> picked.isNotEmpty() && picked.all { cap in capsOf(it) } }
    return SelectionActions(
        extract = false,
        compress = false,
        download = selectionIsFilesOnly(rows, ids) && all("download"),
        delete = all("delete"),
        compressTip = ROOTS_COMPRESS_TIP,
    )
}

/**
 * Tim (2026-09-28): which selection bar actions show for the ticked [ids] of [rows].
 * [sharedCapabilities] null is My Drive, as before: Extract when every pick is extractable,
 * Compress always, Download when every pick is a file, Delete always. In a shared folder they
 * follow the root's grants, where the backend would accept them:
 *
 * - Extract: every pick extractable, with `download` and `upload` (`ArchiveGate.forExtract`:
 *   `download` on the archive, `upload` on its folder, the open one).
 * - Compress: `download` and `upload` (`ArchiveGate.forCompress`: `download` on every input,
 *   `upload` on the first input's folder, the open one).
 * - Download: every pick a file, with `download` (`/files/{id}/content` checks `download`).
 * - Delete: every pick qualifies ([mayDeleteInFolder]): `delete` (`DELETE /api/files/{id}`
 *   checks `delete`, with no owner fence), or, ADV-1412, a row the caller added with `upload`.
 *
 * Example: Lena, an Editor on Run 42, ticks two zips and gets Extract, Compress and Download. If
 * she added both, she gets Delete too. With Kai's samples.csv in the pick, she does not. Priya, a
 * Viewer, gets Download only.
 */
fun selectionActions(rows: List<FileDto>, ids: Set<String>, sharedCapabilities: List<String>?): SelectionActions {
    val extractable = extractableSelection(rows, ids).isNotEmpty()
    val filesOnly = selectionIsFilesOnly(rows, ids)
    val caps = sharedCapabilities ?: return SelectionActions(extractable, compress = true, download = filesOnly, delete = true)
    val writes = "download" in caps && "upload" in caps
    return SelectionActions(
        extract = extractable && writes,
        compress = writes,
        download = filesOnly && "download" in caps,
        delete = rows.filter { it.id in ids }.let { picked -> picked.isNotEmpty() && picked.all { mayDeleteInFolder(it, caps) } },
    )
}

/**
 * ADV-1412: true when the caller may delete [row] inside an open shared folder whose grants are
 * [caps]: with `delete`, or when the caller added the row ([FileDto.mine]) and holds `upload`
 * there, which is the vault's creator arm. A folder the caller added that holds another person's
 * row still shows Delete. The server then answers `403 delete_needs_all_mine`. Example: Lena, an
 * Editor on Run 43, may delete her run43-a.zip but not Kai's samples.csv.
 */
fun mayDeleteInFolder(row: FileDto, caps: List<String>): Boolean =
    "delete" in caps || (row.mine && "upload" in caps)
